import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

interface Statement {
  id: number;
  file_name: string;
  uploaded_at: string;
}

interface Loan {
  id: number;
  company_id: number | null;
  lender_name: string;
  total_amount: number;
  created_at: string;
  updated_at: string;
}

interface ExpenseHead {
  id: number;
  company_id: number | null;
  name: string;
  created_at: string;
  updated_at: string;
}

interface FixedDeposit {
  id: number;
  company_id: number | null;
  fd_number: string;
  total_amount: number;
  created_at: string;
  updated_at: string;
}

interface StatementRow {
  id: number;
  statement_id: number;
  date_str: string;
  particulars: string;
  cheque_no: string;
  debit: number;
  credit: number;
  balance: number;
  mapped_invoice_id: number | null;
  mapped_purchase_id: number | null;
  mapped_loan_id: number | null;
  mapped_expense_head_id: number | null;
  mapped_fd_id: number | null;
  remarks: string | null;
  gst_rate?: number | null;
  gst_amount?: number | null;
  company_id?: number | null;
  bank_account_id?: number | null;
  company_name?: string | null;
  bank_name?: string | null;
  account_number?: string | null;
  is_advance?: boolean;
  mapped_customer_id?: number | null;
  mapped_vendor_id?: number | null;
  tds_amount?: number;
  expense_attachment_url?: string | null;
}
interface Invoice {
  id: number;
  InvoiceNo: string;
  Total_Amount: number;
  amount_paid?: number;
}

interface Purchase {
  id: number;
  InvoiceNo: string;
  Total_Amount: number;
  attachment_url?: string | null;
  amount_paid?: number;
}

const StatementUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StatementRow[]>([]);
  
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [expenseHeads, setExpenseHeads] = useState<ExpenseHead[]>([]);
  const [fixedDeposits, setFixedDeposits] = useState<FixedDeposit[]>([]);

   // Tab state
  const [activeTab, setActiveTab] = useState<'reconciliation' | 'ledger' | 'expenses' | 'history' | 'trash'>('reconciliation');
  const [uploadedStatements, setUploadedStatements] = useState<any[]>([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState<any[][]>([]);
  const [detectedIndices, setDetectedIndices] = useState<any>({
    dateIdx: -1,
    particularsIdx: -1,
    chequeIdx: -1,
    debitIdx: -1,
    creditIdx: -1,
    balanceIdx: -1,
    headerRowIndex: 0
  });
  const [customMapping, setCustomMapping] = useState<any>({
    dateIdx: -1,
    particularsIdx: -1,
    chequeIdx: -1,
    debitIdx: -1,
    creditIdx: -1,
    balanceIdx: -1,
    headerRowIndex: 0
  });
  const [isHighConfidence, setIsHighConfidence] = useState(true);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);

  // Soft deletion and Trash states
  const [deletedRows, setDeletedRows] = useState<any[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deleteWarningRowId, setDeleteWarningRowId] = useState<number | null>(null);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);

  const [expandedLoans, setExpandedLoans] = useState<Record<number, boolean>>({});
  const [loanEntries, setLoanEntries] = useState<Record<number, StatementRow[]>>({});
  
  // Expense Head CRUD states
  const [editingExpenseId, setEditingExpenseId] = useState<number | null>(null);
  const [editingExpenseName, setEditingExpenseName] = useState<string>("");
  const [newExpenseName, setNewExpenseName] = useState<string>("");
  const [newExpenseCompanyId, setNewExpenseCompanyId] = useState<string>("");

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedUploadCompanyId, setSelectedUploadCompanyId] = useState<string>("");
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedUploadBankAccountId, setSelectedUploadBankAccountId] = useState<string>("");

  const [msg, setMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");
  // Helper function to decode JWT token
  const decodeToken = (token: any) => {
    if (!token) return {};
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  };
  const [rowSearch, setRowSearch] = useState<Record<number, string>>({});
  const [editMode, setEditMode] = useState<Record<number, boolean>>({});
  const [tempInvoice, setTempInvoice] = useState<Record<number, number | string | null>>({});
  const [tempInvoiceCustomer, setTempInvoiceCustomer] = useState<Record<number, string>>({});
  const [tempInvoiceNo, setTempInvoiceNo] = useState<Record<number, string>>({});
  const [tempPurchase, setTempPurchase] = useState<Record<number, number | string | null>>({});
  const [tempPurchaseVendor, setTempPurchaseVendor] = useState<Record<number, string>>({});
  const [tempPurchaseNo, setTempPurchaseNo] = useState<Record<number, string>>({});
  const [tempLoanId, setTempLoanId] = useState<Record<number, number | string | null>>({});
  const [tempLenderName, setTempLenderName] = useState<Record<number, string>>({});
  const [tempExpenseHeadId, setTempExpenseHeadId] = useState<Record<number, number | null>>({});
  const [tempExpenseHeadName, setTempExpenseHeadName] = useState<Record<number, string>>({});
  const [tempFDId, setTempFDId] = useState<Record<number, number | null>>({});
  const [tempFDNumber, setTempFDNumber] = useState<Record<number, string>>({});
  const [tempIsGstExpense, setTempIsGstExpense] = useState<Record<number, boolean>>({});
  const [tempGstRate, setTempGstRate] = useState<Record<number, number | null>>({});
  const [mappingMode, setMappingMode] = useState<Record<number, 'invoice' | 'purchase' | 'loan' | 'expense' | 'fd' | 'advance'>>({});
  const [tempRemarks, setTempRemarks] = useState<Record<number, string>>({});
  const [tempMappingRows, setTempMappingRows] = useState<Record<number, boolean>>({});
  const [clubMode, setClubMode] = useState<Record<number, boolean>>({});
  const [selectedClubInvoiceIds, setSelectedClubInvoiceIds] = useState<Record<number, number[]>>({});
  const [gatewayFee, setGatewayFee] = useState<Record<number, number | string>>({});
  const [tdsInputVal, setTdsInputVal] = useState<string>("");
  const [tempTdsExpense, setTempTdsExpense] = useState<Record<number, number | string>>({});
  const [tempTdsRateExpense, setTempTdsRateExpense] = useState<Record<number, number | string>>({});
  const [tempExpenseAttachment, setTempExpenseAttachment] = useState<Record<number, string | null>>({});
  const [tempCustomerId, setTempCustomerId] = useState<Record<number, number | null>>({});
  const [tempVendorId, setTempVendorId] = useState<Record<number, number | null>>({});
  const [warningModal, setWarningModal] = useState<{
    isOpen: boolean;
    type: 'credit' | 'debit';
    rowId: number;
    invoiceId?: number | null;
    purchaseId?: number | null;
    remarks?: string | null;
    invoiceNo?: string;
    invoiceAmount?: number;
    receivedAmount?: number;
    difference?: number;
    gatewayFee?: number;
    mappedCustomerId?: number | null;
    mappedVendorId?: number | null;
    isAdvance?: boolean;
    pendingAmount?: number;
    isExcess?: boolean;
  } | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [filterCompanyId, setFilterCompanyId] = useState<string>("");
  const [filterMappingStatus, setFilterMappingStatus] = useState<'all' | 'mapped' | 'unmapped'>('all');

  const getMonthYear = (dateStr: string) => {
    if (!dateStr) return { month: "", year: "" };
    const parts = dateStr.split(/[-/\s.]+/);
    if (parts.length >= 3) {
      let monthPart = parts[1];
      let yearVal = parts[2];
      
      // If year is at the beginning (ISO format YYYY-MM-DD)
      if (parts[0].length === 4) {
        yearVal = parts[0];
        monthPart = parts[1];
      }

      let year = yearVal;
      if (year && year.length === 2) {
        year = "20" + year;
      }
      
      let monthName = monthPart;
      if (/^\d+$/.test(monthPart)) {
        const monthNum = parseInt(monthPart, 10);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        if (monthNum >= 1 && monthNum <= 12) {
          monthName = months[monthNum - 1];
        }
      }
      if (monthName.length > 0) {
        monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1).toLowerCase();
      }
      return { month: monthName, year };
    }
    return { month: "", year: "" };
  };

  const convertToISODate = (dmy: string): string => {
    if (!dmy) return new Date().toISOString().split('T')[0];
    const parts = dmy.split(/[-/.]/);
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) year = '20' + year;
      return `${year}-${month}-${day}`;
    }
    return dmy;
  };

  const fetchAllRows = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const res = await axios.get(`${API_URL}/auth/bank-statement-rows`);
      setRows(res.data || []);
    } catch {
      setErrMsg("Failed to fetch statement transactions.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedStatements = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/bank-statements`);
      setUploadedStatements(res.data || []);
    } catch (err) {
      console.error("Failed to load uploaded statements:", err);
    }
  };

  const handleDeleteStatement = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this bank statement? This will permanently delete all associated transaction entries and clear their mappings!")) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/auth/bank-statement/${id}`);
      fetchUploadedStatements();
      fetchAllRows();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to delete statement.");
    }
  };

  const handleDeleteStatementRow = (id: number) => {
    setDeleteWarningRowId(id);
    setShowDeleteWarningModal(true);
  };

  const handleConfirmDeleteRow = async () => {
    if (!deleteWarningRowId) return;
    try {
      await axios.delete(`${API_URL}/auth/bank-statement-row/${deleteWarningRowId}`);
      setMsg("Transaction row moved to Trash successfully!");
      setShowDeleteWarningModal(false);
      setDeleteWarningRowId(null);
      await fetchAllRows();
      await fetchLoans();
      await fetchExpenseHeads();
      await fetchFixedDeposits();
      await fetchDeletedRows();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to delete transaction row.");
    }
  };

  const fetchDeletedRows = async () => {
    setDeletedLoading(true);
    try {
      const res = await axios.get(`${API_URL}/auth/bank-statement-rows/deleted`);
      setDeletedRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load deleted rows:", err);
    } finally {
      setDeletedLoading(false);
    }
  };

  const handleRestoreRow = async (id: number) => {
    try {
      await axios.post(`${API_URL}/auth/bank-statement-row/${id}/restore`);
      setMsg("Transaction row retrieved successfully!");
      await fetchDeletedRows();
      await fetchAllRows();
      await fetchLoans();
      await fetchExpenseHeads();
      await fetchFixedDeposits();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to restore transaction row.");
    }
  };

  const fetchLoans = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/loans`);
      setLoans(res.data || []);
    } catch (err) {
      console.error("Failed to load loans:", err);
    }
  };

  const fetchExpenseHeads = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/expense-heads`);
      setExpenseHeads(res.data || []);
    } catch (err) {
      console.error("Failed to load expense heads:", err);
    }
  };

  const fetchFixedDeposits = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/fixed-deposits`);
      setFixedDeposits(res.data || []);
    } catch (err) {
      console.error("Failed to load fixed deposits:", err);
    }
  };

  const fetchMappingTargets = async () => {
    try {
      const [invRes, purRes, custRes, vendRes] = await Promise.all([
        axios.get(`${API_URL}/auth/invoices`),
        axios.get(`${API_URL}/auth/purchase-invoices`),
        axios.get(`${API_URL}/auth/customers`),
        axios.get(`${API_URL}/auth/vendors`)
      ]);
      setInvoices(invRes.data || []);
      setPurchases(purRes.data || []);
      setCustomers(custRes.data || []);
      setVendors(vendRes.data || []);
    } catch {
      setErrMsg("Failed to fetch invoices/purchases for mapping.");
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const storedRole = localStorage.getItem("role");
      let decoded: any = null;
      if (token) {
        decoded = decodeToken(token);
      }

      let compId: string | number | null = null;
      if (decoded) {
        compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
      }

      const res = await axios.get(`${API_URL}/auth/companies`);
      const allComps = res.data || [];
      if (storedRole !== "super-admin" && compId) {
        setCompanies(allComps.filter((c: any) => String(c.id) === String(compId)));
      } else {
        setCompanies(allComps);
      }
    } catch (err) {
      console.error("Failed to load companies", err);
    }
  };

  const handleCompanyChange = async (companyId: string) => {
    setSelectedUploadCompanyId(companyId);
    setSelectedUploadBankAccountId("");
    if (companyId) {
      try {
        const res = await axios.get(`${API_URL}/auth/company/${companyId}/bank`);
        setBankAccounts(res.data || []);
      } catch (err) {
        console.error("Failed to load bank accounts", err);
        setBankAccounts([]);
      }
    } else {
      setBankAccounts([]);
    }
  };

  const handleFilterCompanyChange = (companyId: string) => {
    setFilterCompanyId(companyId);
    handleCompanyChange(companyId);
  };

  useEffect(() => {
    fetchAllRows();
    fetchMappingTargets();
    fetchCompanies().then(() => {
      const token = localStorage.getItem("token");
      const storedRole = localStorage.getItem("role");
      let decoded: any = null;
      if (token) {
        decoded = decodeToken(token);
      }
      let compId: string | number | null = null;
      if (decoded) {
        compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
      }
      if (storedRole !== "super-admin" && compId) {
        setFilterCompanyId(String(compId));
        setSelectedUploadCompanyId(String(compId));
        // Also fetch bank accounts for upload
        axios.get(`${API_URL}/auth/company/${compId}/bank`).then((res) => {
          setBankAccounts(res.data || []);
        }).catch(() => {});
      } else {
        const selectedCompanyHeader = localStorage.getItem("selected_company_id");
        if (selectedCompanyHeader && selectedCompanyHeader !== "all") {
          setFilterCompanyId(selectedCompanyHeader);
          setSelectedUploadCompanyId(selectedCompanyHeader);
          axios.get(`${API_URL}/auth/company/${selectedCompanyHeader}/bank`).then((res) => {
            setBankAccounts(res.data || []);
          }).catch(() => {});
        }
      }
    });
    fetchLoans();
    fetchExpenseHeads();
    fetchFixedDeposits();
    fetchUploadedStatements();
  }, []);

  useEffect(() => {
    if (rows.length > 0 && !selectedMonth && !selectedYear) {
      const latestRow = rows[0]; // ordered by id DESC
      const { month, year } = getMonthYear(latestRow.date_str);
      if (month) setSelectedMonth(month);
      if (year) setSelectedYear(year);
    }
  }, [rows]);

  const { months, years } = useMemo(() => {
    const monthsSet = new Set<string>();
    const yearsSet = new Set<string>();
    rows.forEach((r: StatementRow) => {
      const { month, year } = getMonthYear(r.date_str);
      if (month) monthsSet.add(month);
      if (year) yearsSet.add(year);
    });
    const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const sortedMonths = Array.from(monthsSet).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
    const sortedYears = Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
    return { months: sortedMonths, years: sortedYears };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const token = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    let decoded: any = null;
    if (token) {
      decoded = decodeToken(token);
    }
    let compId: string | number | null = null;
    if (decoded) {
      compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
    }

    return rows.filter((r: StatementRow) => {
      // 1. Scoping Check: Company accounts must only see their own bank statement rows
      if (storedRole !== "super-admin" && compId) {
        if (String(r.company_id) !== String(compId)) {
          return false;
        }
      }

      const { month, year } = getMonthYear(r.date_str);
      const matchMonth = !selectedMonth || month === selectedMonth;
      const matchYear = !selectedYear || year === selectedYear;
      const matchCompany = !filterCompanyId || String(r.company_id) === String(filterCompanyId);
      
      const isMapped = !!(r.mapped_invoice_id || r.mapped_purchase_id || r.mapped_loan_id || r.mapped_expense_head_id || r.mapped_fd_id);
      const matchMapping = 
        filterMappingStatus === 'all' ||
        (filterMappingStatus === 'mapped' && isMapped) ||
        (filterMappingStatus === 'unmapped' && !isMapped);

      return matchMonth && matchYear && matchCompany && matchMapping;
    });
  }, [rows, selectedMonth, selectedYear, filterCompanyId, filterMappingStatus]);

  const unmappedCount = useMemo(() => {
    return rows.filter((r: StatementRow) => {
      const { month, year } = getMonthYear(r.date_str);
      const matchMonth = !selectedMonth || month === selectedMonth;
      const matchYear = !selectedYear || year === selectedYear;
      const matchCompany = !filterCompanyId || String(r.company_id) === String(filterCompanyId);
      const isMapped = !!(r.mapped_invoice_id || r.mapped_purchase_id || r.mapped_loan_id || r.mapped_expense_head_id || r.mapped_fd_id);
      return matchMonth && matchYear && matchCompany && !isMapped;
    }).length;
  }, [rows, selectedMonth, selectedYear, filterCompanyId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setMsg("");
      setErrMsg("");
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setErrMsg("Please select a file first.");
      return;
    }
    setLoading(true);
    setMsg("");
    setErrMsg("");

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/auth/preview-statement`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = res.data;
      setPreviewRows(data.previewRows || []);
      setDetectedIndices(data.detectedIndices);
      setCustomMapping(data.detectedIndices);
      setIsHighConfidence(data.highConfidence);
      setPendingUploadFile(file);
      setPreviewModalOpen(true);
    } catch (err: any) {
      setErrMsg(err?.response?.data?.error || "Failed to preview statement. Make sure the file format is correct.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!pendingUploadFile) return;
    setLoading(true);
    setMsg("");
    setErrMsg("");
    setPreviewModalOpen(false);

    const formData = new FormData();
    formData.append('file', pendingUploadFile);
    if (selectedUploadCompanyId) {
      formData.append('company_id', selectedUploadCompanyId);
    }
    if (selectedUploadBankAccountId) {
      formData.append('bank_account_id', selectedUploadBankAccountId);
    }
    
    formData.append('dateIdx', String(customMapping.dateIdx));
    formData.append('particularsIdx', String(customMapping.particularsIdx));
    formData.append('chequeIdx', String(customMapping.chequeIdx));
    formData.append('debitIdx', String(customMapping.debitIdx));
    formData.append('creditIdx', String(customMapping.creditIdx));
    formData.append('balanceIdx', String(customMapping.balanceIdx));
    formData.append('headerRowIndex', String(customMapping.headerRowIndex));

    try {
      await axios.post(`${API_URL}/auth/upload-statement`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setMsg("Bank statement uploaded and saved successfully!");
      setFile(null);
      setPendingUploadFile(null);
      setSelectedUploadCompanyId("");
      setSelectedUploadBankAccountId("");
      setBankAccounts([]);
      await fetchAllRows();
      fetchUploadedStatements();
    } catch (err: any) {
      setErrMsg(err?.response?.data?.error || "Failed to upload and parse statement.");
    } finally {
      setLoading(false);
    }
  };

  const handleMapRow = async (
    rowId: number,
    invoiceId: number | null,
    purchaseId: number | null,
    loanId: number | null,
    lenderName?: string,
    expenseHeadId?: number | null,
    expenseHeadName?: string,
    remarks?: string | null,
    tdsAmount?: number,
    isPartial?: boolean,
    fdId?: number | null,
    fdNumber?: string,
    gstRate?: number | null,
    gstAmount?: number | null,
    expenseAttachmentUrl?: string | null,
    mappedInvoiceIds?: number[],
    gatewayDeduction?: number,
    mappedCustomerId?: number | null,
    mappedVendorId?: number | null,
    isAdvance?: boolean
  ) => {
    if (tempMappingRows[rowId]) return;
    setTempMappingRows(prev => ({ ...prev, [rowId]: true }));
    try {
      await axios.post(`${API_URL}/auth/bank-statement-row/${rowId}/map`, {
        mapped_invoice_id: invoiceId,
        mapped_purchase_id: purchaseId,
        mapped_loan_id: loanId,
        lender_name: lenderName,
        mapped_expense_head_id: expenseHeadId,
        expense_head_name: expenseHeadName,
        mapped_fd_id: fdId,
        fd_number: fdNumber,
        remarks: remarks !== undefined ? remarks : null,
        tds_amount: tdsAmount,
        is_partial: isPartial,
        gst_rate: gstRate,
        gst_amount: gstAmount,
        mapped_invoice_ids: mappedInvoiceIds,
        gateway_deduction: gatewayDeduction,
        mapped_customer_id: mappedCustomerId,
        mapped_vendor_id: mappedVendorId,
        is_advance: isAdvance,
        expense_attachment_url: expenseAttachmentUrl
      });
      // Refresh all data
      await fetchAllRows();
      await fetchLoans();
      await fetchExpenseHeads();
      await fetchFixedDeposits();
    } catch (err: any) {
      console.error("Mapping error details:", err);
      alert(err?.response?.data?.error || "Failed to map transaction.");
    } finally {
      setTempMappingRows(prev => ({ ...prev, [rowId]: false }));
    }
  };

  const toggleLoanExpand = async (loanId: number) => {
    if (expandedLoans[loanId]) {
      setExpandedLoans(prev => ({ ...prev, [loanId]: false }));
    } else {
      try {
        const res = await axios.get(`${API_URL}/auth/loans/${loanId}/entries`);
        setLoanEntries(prev => ({ ...prev, [loanId]: res.data || [] }));
        setExpandedLoans(prev => ({ ...prev, [loanId]: true }));
      } catch (err) {
        console.error("Failed to load loan entries", err);
        alert("Failed to load loan details.");
      }
    }
  };
  const handleAddExpenseHead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExpenseName.trim()) return;
    try {
      const compId = newExpenseCompanyId || selectedUploadCompanyId || localStorage.getItem("selected_company_id");
      await axios.post(`${API_URL}/auth/expense-heads`, {
        name: newExpenseName,
        company_id: compId ? Number(compId) : null
      });
      setNewExpenseName("");
      setNewExpenseCompanyId("");
      setMsg("Expense head added successfully!");
      fetchExpenseHeads();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to add expense head.");
    }
  };

  const handleEditExpenseHead = async (id: number) => {
    if (!editingExpenseName.trim()) return;
    try {
      await axios.put(`${API_URL}/auth/expense-heads/${id}`, {
        name: editingExpenseName
      });
      setEditingExpenseId(null);
      setEditingExpenseName("");
      setMsg("Expense head updated successfully!");
      fetchExpenseHeads();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to update expense head.");
    }
  };

  const handleDeleteExpenseHead = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this expense head? Any mapped transactions will be unmapped.")) return;
    try {
      await axios.delete(`${API_URL}/auth/expense-heads/${id}`);
      setMsg("Expense head deleted successfully!");
      fetchExpenseHeads();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to delete expense head.");
    }
  };

  return (
    <div className="w-full px-6 py-10">
      <div className="mb-8">
        <h2 className="text-3xl font-extrabold text-blue-800">Bank Statement Reconciliation</h2>
        <p className="text-gray-600 mt-2">Upload bank statements in XLSX/Excel format, parse statements, and map credits to sales invoices and debits to purchase bills.</p>
      </div>

      {/* Tab Selectors */}
      <div className="flex gap-4 mb-8 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('reconciliation')}
          className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'reconciliation' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          📂 Statement Reconciliation
        </button>
        <button
          onClick={() => setActiveTab('ledger')}
          className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'ledger' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          🤝 Loans Ledger
        </button>
        <button
          onClick={() => setActiveTab('expenses')}
          className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'expenses' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          🏷️ Expense Heads
        </button>
        <button
          onClick={() => { setActiveTab('history'); fetchUploadedStatements(); }}
          className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'history' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          📄 Statement History
        </button>
        <button
          onClick={() => { setActiveTab('trash'); fetchDeletedRows(); }}
          className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'trash' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          🗑️ Deleted Data (Trash)
        </button>
      </div>


      {activeTab === 'reconciliation' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Upload Bank Statement */}
            <div className="md:col-span-2 bg-white shadow-xl border border-blue-200 rounded-2xl p-6">
              <h3 className="font-bold text-blue-900 mb-4 text-lg">Upload Bank Statement</h3>
              <form onSubmit={handleUpload} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Company</label>
                  <select
                    value={selectedUploadCompanyId}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Bank Account</label>
                  <select
                    value={selectedUploadBankAccountId}
                    onChange={(e) => setSelectedUploadBankAccountId(e.target.value)}
                    className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Bank Account</option>
                    {bankAccounts.map((b: any) => (
                      <option key={b.id} value={b.id}>{b.bank_name} ({b.account_number})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Choose File (.xls, .xlsx)</label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept=".xls,.xlsx"
                      onChange={handleFileChange}
                      className="w-full border-2 border-blue-100 rounded-xl bg-white p-1 text-xs font-semibold text-blue-950 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                      required
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-extrabold uppercase transition-all duration-150 shadow-md flex-shrink-0"
                    >
                      {loading ? '...' : 'Upload'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Filter Transactions by Month and Year */}
            <div className="bg-white shadow-xl border border-blue-200 rounded-2xl p-6">
              <h3 className="font-bold text-blue-900 mb-4 text-lg">Filter Transactions</h3>
              <div className="flex flex-col gap-4">
                {companies.length > 1 && (
                  <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Company</label>
                    <select
                      value={filterCompanyId}
                      onChange={e => handleFilterCompanyChange(e.target.value)}
                      className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Companies</option>
                      {companies.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.company_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60">Year</label>
                    <select
                      value={selectedYear}
                      onChange={e => setSelectedYear(e.target.value)}
                      className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
                    >
                      <option value="">All Years</option>
                      {years.map((y: string) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60">Month</label>
                    <select
                      value={selectedMonth}
                      onChange={e => setSelectedMonth(e.target.value)}
                      className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
                    >
                      <option value="">All Months</option>
                      {months.map((m: string) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60">Status</label>
                    <select
                      value={filterMappingStatus}
                      onChange={e => setFilterMappingStatus(e.target.value as any)}
                      className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2.5 font-semibold text-blue-950"
                    >
                      <option value="all">All</option>
                      <option value="mapped">Mapped</option>
                      <option value="unmapped">Unmapped</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {msg && <div className="mb-6 p-3 bg-green-50 text-green-700 rounded-xl border border-green-200 text-center shadow font-semibold">{msg}</div>}
          {errMsg && <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-xl border border-red-200 text-center shadow font-semibold">{errMsg}</div>}

          {/* Transactions Review and Reconciliation Table */}
          {rows.length > 0 && (
            <div className="bg-white shadow-xl rounded-2xl border overflow-hidden">
              <div className="p-5 bg-blue-50 border-b border-gray-100 flex justify-between items-center flex-wrap gap-2">
                <h3 className="font-extrabold text-blue-900 text-lg">Statement reconciliation</h3>
                <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">{filteredRows.length} rows found</span>
              </div>
              {filterMappingStatus === 'mapped' && unmappedCount > 0 && (
                <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex justify-between items-center text-xs text-amber-800 font-semibold">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>You have <strong>{unmappedCount}</strong> unmapped transaction{unmappedCount > 1 ? 's' : ''} in the selected period.</span>
                  </div>
                  <button
                    onClick={() => setFilterMappingStatus('unmapped')}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-extrabold px-3 py-1.5 rounded-lg transition-colors shadow-sm text-[11px] uppercase tracking-wider"
                  >
                    View Unmapped Entries
                  </button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse table-fixed text-[11px]">
                  <thead>
                    <tr className="bg-gray-50 border-b text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      <th className="p-2 w-[12%]">Company</th>
                      <th className="p-2 w-[15%]">Bank Account</th>
                      <th className="p-2 w-[8%]">Date</th>
                      <th className="p-2 w-[22%]">Particulars</th>
                      <th className="p-2 text-right w-[9%]">Debit (₹)</th>
                      <th className="p-2 text-right w-[9%]">Credit (₹)</th>
                      <th className="p-2 w-[10%]">Remarks</th>
                      <th className="p-2 w-[20%]">Mapping (Reconciliation)</th>
                      <th className="p-2 text-center w-[5%]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredRows.map((row: StatementRow) => (
                      <tr key={row.id} className="hover:bg-blue-50/10 transition-colors">
                        <td className="p-2 truncate font-bold text-blue-900" title={row.company_name || 'Global'}>{row.company_name || <span className="text-gray-400 italic">Global</span>}</td>
                        <td className="p-2 truncate font-medium text-gray-700" title={row.bank_name ? `${row.bank_name} (${row.account_number})` : ''}>
                          {row.bank_name ? `${row.bank_name} (${row.account_number})` : <span className="text-gray-400 italic">—</span>}
                        </td>
                        <td className="p-2 whitespace-nowrap font-medium text-gray-700">{row.date_str}</td>
                        <td className="p-2 text-gray-600 truncate max-w-[200px]" title={row.particulars}>{row.particulars}</td>
                        <td className="p-2 text-right font-medium text-red-600">
                          {row.debit > 0 ? `₹${row.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "—"}
                        </td>
                        <td className="p-2 text-right font-medium text-green-600">
                          {row.credit > 0 ? `₹${row.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "—"}
                        </td>
                        <td className="p-2">
                          {editMode[row.id] || (!row.mapped_invoice_id && !row.mapped_purchase_id && !row.mapped_loan_id && !row.mapped_expense_head_id && !row.mapped_fd_id && !row.mapped_customer_id && !row.mapped_vendor_id) ? (
                            <input
                              type="text"
                              placeholder="Add a remark..."
                              value={tempRemarks[row.id] !== undefined ? tempRemarks[row.id] : (row.remarks || "")}
                              onChange={e => setTempRemarks(prev => ({ ...prev, [row.id]: e.target.value }))}
                              className="w-full border border-gray-300 rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50 text-gray-900"
                            />
                          ) : (
                            <span className="text-gray-700 text-[11px] font-semibold">{row.remarks || "—"}</span>
                          )}
                        </td>
                        <td className="p-2">
                          {/* Mapping UI depending on credit vs debit */}
                          {row.credit > 0 ? (
                            row.mapped_invoice_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-green-50 border-2 border-green-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-green-950 truncate max-w-[170px]" title={(() => {
                                  const inv = invoices.find(i => i.id === row.mapped_invoice_id);
                                  const targetInv: any = inv;
                                  const customerObj = customers.find(c => String(c.id) === String(targetInv?.Customer_id));
                                  return `${inv?.InvoiceNo || ''}${customerObj ? ` - ${customerObj.company_name}` : ''} (₹${inv?.Total_Amount || 0})`;
                                })()}>
                                  Mapped: {(() => {
                                    const inv = invoices.find(i => i.id === row.mapped_invoice_id);
                                    const targetInv: any = inv;
                                    const customerObj = customers.find(c => String(c.id) === String(targetInv?.Customer_id));
                                    return `${inv?.InvoiceNo || ''}${customerObj ? ` - ${customerObj.company_name}` : ''} (₹${inv?.Total_Amount || 0})`;
                                  })()}
                                </span>
                                {row.mapped_invoice_id && (
                                  <a
                                    href={`${API_URL}/uploads/invoice_${row.mapped_invoice_id}.pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[10px] text-blue-600 font-bold underline block mt-0.5"
                                  >
                                    📄 View Sales Invoice
                                  </a>
                                )}
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'invoice' }));
                                    setTempInvoice(prev => ({ ...prev, [row.id]: row.mapped_invoice_id }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : row.mapped_loan_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-purple-50 border-2 border-purple-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-purple-950 truncate max-w-[170px]" title={(() => {
                                  const ln = loans.find(l => l.id === row.mapped_loan_id);
                                  return `Mapped Loan: ${ln?.lender_name || ''} (₹${row.credit})`;
                                })()}>
                                  Mapped Loan: {(() => {
                                    const ln = loans.find(l => l.id === row.mapped_loan_id);
                                    return ln?.lender_name || 'Loan';
                                  })()}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'loan' }));
                                    setTempLoanId(prev => ({ ...prev, [row.id]: row.mapped_loan_id }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : row.is_advance && row.mapped_customer_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-yellow-50 border-2 border-yellow-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-yellow-950 truncate max-w-[170px]" title={(() => {
                                  const cust = customers.find(c => c.id === row.mapped_customer_id);
                                  return `Customer Advance: ${cust?.company_name || ''} (₹${row.credit})`;
                                })()}>
                                  Customer Advance: {(() => {
                                    const cust = customers.find(c => c.id === row.mapped_customer_id);
                                    return cust?.company_name || 'Customer';
                                  })()}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'advance' }));
                                    setTempCustomerId(prev => ({ ...prev, [row.id]: row.mapped_customer_id || null }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : row.is_advance && row.mapped_vendor_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-yellow-50 border-2 border-yellow-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-yellow-950 truncate max-w-[170px]" title={(() => {
                                  const vend = vendors.find(v => v.id === row.mapped_vendor_id);
                                  return `Vendor Advance: ${vend?.company_name || ''} (₹${row.debit})`;
                                })()}>
                                  Vendor Advance: {(() => {
                                    const vend = vendors.find(v => v.id === row.mapped_vendor_id);
                                    return vend?.company_name || 'Vendor';
                                  })()}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'advance' }));
                                    setTempVendorId(prev => ({ ...prev, [row.id]: row.mapped_vendor_id || null }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : row.mapped_fd_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-cyan-50 border-2 border-cyan-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-cyan-950 truncate max-w-[170px]" title={(() => {
                                  const fd = fixedDeposits.find(f => f.id === row.mapped_fd_id);
                                  return `Mapped FD Mature: ${fd?.fd_number || ''} (₹${row.credit})`;
                                })()}>
                                  Mapped FD: {(() => {
                                    const fd = fixedDeposits.find(f => f.id === row.mapped_fd_id);
                                    return fd?.fd_number || 'FD';
                                  })()}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'fd' }));
                                    setTempFDId(prev => ({ ...prev, [row.id]: row.mapped_fd_id }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1.5 min-w-[200px]">
                                <select
                                  value={mappingMode[row.id] || (row.is_advance ? 'advance' : row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : 'invoice')}
                                  onChange={e => setMappingMode(prev => ({ ...prev, [row.id]: e.target.value as any }))}
                                  className="form-select border border-gray-300 rounded-lg p-1 text-xs bg-white text-gray-700 w-full"
                                >
                                  <option value="invoice">Map to Invoice (Sales)</option>
                                  <option value="loan">Map to Loan (Receipt)</option>
                                  <option value="fd">Map to Fixed Deposit (Mature)</option>
                                  <option value="advance">Customer Advance Payment</option>
                                </select>
 
                                {(mappingMode[row.id] || (row.is_advance ? 'advance' : row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : 'invoice')) === 'advance' ? (
                                  <>
                                    <select
                                      value={tempCustomerId[row.id] !== undefined ? (tempCustomerId[row.id] || "") : (row.mapped_customer_id || "")}
                                      onChange={e => setTempCustomerId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border border-cyan-300 text-cyan-900 rounded-lg p-1.5 text-xs bg-cyan-50 font-bold w-full"
                                    >
                                      <option value="">Select Customer for Advance...</option>
                                      {customers
                                        .filter(c => !row.company_id || String(c.company_id) === String(row.company_id))
                                        .map(c => (
                                          <option key={c.id} value={c.id}>{c.company_name}</option>
                                        ))}
                                    </select>
                                  </>
                                ) : (mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : 'invoice')) === 'invoice' ? (
                                  <>
                                    <input
                                      type="text"
                                      placeholder="Type to search Invoice..."
                                      value={rowSearch[row.id] || ""}
                                      onChange={e => setRowSearch(prev => ({ ...prev, [row.id]: e.target.value }))}
                                      className="w-full border border-green-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-green-50/20 text-green-900"
                                    />
                                    <div className="flex items-center gap-1.5 mt-1 mb-1">
                                      <input
                                        type="checkbox"
                                        id={`club-mode-${row.id}`}
                                        checked={!!clubMode[row.id]}
                                        onChange={e => {
                                          const checked = e.target.checked;
                                          setClubMode(prev => ({ ...prev, [row.id]: checked }));
                                          if (!checked) {
                                            setSelectedClubInvoiceIds(prev => ({ ...prev, [row.id]: [] }));
                                          }
                                        }}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-3 w-3"
                                      />
                                      <label htmlFor={`club-mode-${row.id}`} className="text-[10px] font-semibold text-green-800 cursor-pointer">
                                        Club Multiple Invoices (Lumpsum)
                                      </label>
                                    </div>

                                    {clubMode[row.id] ? (
                                      <div className="border border-green-200 rounded-lg p-2 max-h-32 overflow-y-auto bg-green-50/30 text-[10px] space-y-1">
                                        {invoices
                                          .filter((inv: Invoice) => {
                                            const targetInv: any = inv;
                                            const isSearched = inv.InvoiceNo.toLowerCase().includes((rowSearch[row.id] || "").toLowerCase());
                                            const isSameCompany = !row.company_id || String(targetInv.Company_id) === String(row.company_id);
                                            return isSearched && isSameCompany;
                                          })
                                          .map((inv: Invoice) => {
                                            const targetInv: any = inv;
                                            const customerObj = customers.find(c => String(c.id) === String(targetInv.Customer_id));
                                            const custNameStr = customerObj ? ` - ${customerObj.company_name}` : '';
                                            const isChecked = (selectedClubInvoiceIds[row.id] || []).includes(inv.id);
                                            return (
                                              <label key={inv.id} className="flex items-center gap-1.5 py-0.5 cursor-pointer hover:bg-green-100/50 px-1 rounded">
                                                <input
                                                  type="checkbox"
                                                  checked={isChecked}
                                                  onChange={e => {
                                                    const checked = e.target.checked;
                                                    setSelectedClubInvoiceIds(prev => {
                                                      const current = prev[row.id] || [];
                                                      if (checked) {
                                                        return { ...prev, [row.id]: [...current, inv.id] };
                                                      } else {
                                                        return { ...prev, [row.id]: current.filter(id => id !== inv.id) };
                                                      }
                                                    });
                                                  }}
                                                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-3 w-3"
                                                />
                                                <span className="font-semibold text-green-950 leading-tight">
                                                  {inv.InvoiceNo}{custNameStr} (₹{inv.Total_Amount})
                                                </span>
                                              </label>
                                            );
                                          })}
                                      </div>
                                    ) : (
                                      <select
                                        value={tempInvoice[row.id] !== undefined ? (tempInvoice[row.id] || "") : (row.mapped_invoice_id || "")}
                                        onChange={e => setTempInvoice(prev => ({ ...prev, [row.id]: e.target.value === 'NEW' ? 'NEW' : (e.target.value ? Number(e.target.value) : null) }))}
                                        className="form-select border-2 border-green-200 text-green-900 rounded-lg p-1.5 text-xs bg-green-50 font-bold w-full"
                                      >
                                        <option value="">Map to Invoice (Sales)</option>
                                        {invoices
                                          .filter((inv: Invoice) => {
                                            const targetInv: any = inv;
                                            const isSearched = inv.InvoiceNo.toLowerCase().includes((rowSearch[row.id] || "").toLowerCase());
                                            const isSameCompany = !row.company_id || String(targetInv.Company_id) === String(row.company_id);
                                            return isSearched && isSameCompany;
                                          })
                                          .map((inv: Invoice) => {
                                            const targetInv: any = inv;
                                            const customerObj = customers.find(c => String(c.id) === String(targetInv.Customer_id));
                                            const custNameStr = customerObj ? ` - ${customerObj.company_name}` : '';
                                            return (
                                              <option key={inv.id} value={inv.id}>
                                                {inv.InvoiceNo}{custNameStr} (₹{inv.Total_Amount})
                                              </option>
                                            );
                                          })}
                                        <option value="NEW">Create New Invoice...</option>
                                      </select>
                                    )}

                                    <div className="mt-1">
                                      <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">
                                        Gateway Fee / Deduction
                                      </label>
                                      <input
                                        type="number"
                                        placeholder="Gateway fees if any..."
                                        value={gatewayFee[row.id] !== undefined ? gatewayFee[row.id] : ""}
                                        onChange={e => setGatewayFee(prev => ({ ...prev, [row.id]: e.target.value }))}
                                        className="w-full border border-gray-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500 font-semibold bg-white text-gray-950"
                                      />
                                    </div>

                                    {String(tempInvoice[row.id]) === 'NEW' && (
                                      <div className="flex flex-col gap-1.5 mt-1 border-t pt-1 border-green-200">
                                        <select
                                          value={tempInvoiceCustomer[row.id] || ""}
                                          onChange={e => setTempInvoiceCustomer(prev => ({ ...prev, [row.id]: e.target.value }))}
                                          className="form-select border border-green-300 text-green-950 rounded p-1 text-xs bg-white w-full font-bold"
                                        >
                                          <option value="">Select Customer *</option>
                                          {customers
                                            .filter(c => !row.company_id || String(c.company_id) === String(row.company_id))
                                            .map(c => (
                                              <option key={c.id} value={c.id}>{c.company_name}</option>
                                            ))}
                                        </select>
                                        <input
                                          type="text"
                                          placeholder="Invoice Number *"
                                          value={tempInvoiceNo[row.id] || ""}
                                          onChange={e => setTempInvoiceNo(prev => ({ ...prev, [row.id]: e.target.value }))}
                                          className="w-full border border-green-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-white text-green-950 font-bold"
                                        />
                                      </div>
                                    )}
                                  </>
                                ) : (mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : 'invoice')) === 'loan' ? (
                                  <>
                                    <select
                                      value={tempLoanId[row.id] !== undefined ? (tempLoanId[row.id] || "") : (row.mapped_loan_id || "")}
                                      onChange={e => setTempLoanId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border-2 border-purple-200 text-purple-900 rounded-lg p-1.5 text-xs bg-purple-50 font-bold w-full"
                                    >
                                      <option value="">Select Existing Lender...</option>
                                      {loans
                                        .filter(l => !row.company_id || String(l.company_id) === String(row.company_id))
                                        .map(l => (
                                          <option key={l.id} value={l.id}>
                                            {l.lender_name} (Balance: ₹{l.total_amount})
                                          </option>
                                        ))}
                                      <option value="NEW">Create New Lender / Person...</option>
                                    </select>
 
                                    {(tempLoanId[row.id] === null || String(tempLoanId[row.id]) === 'NEW' || !tempLoanId[row.id]) && (
                                      <input
                                        type="text"
                                        placeholder="Enter Lender / Person Name..."
                                        value={tempLenderName[row.id] || ""}
                                        onChange={e => setTempLenderName(prev => ({ ...prev, [row.id]: e.target.value }))}
                                        className="w-full border border-purple-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-purple-50/20 text-purple-900"
                                      />
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <select
                                      value={tempFDId[row.id] !== undefined ? (tempFDId[row.id] || "") : (row.mapped_fd_id || "")}
                                      onChange={e => setTempFDId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border-2 border-cyan-200 text-cyan-900 rounded-lg p-1.5 text-xs bg-cyan-50 font-bold w-full"
                                    >
                                      <option value="">Select Existing FD...</option>
                                      {fixedDeposits
                                        .filter(f => !row.company_id || String(f.company_id) === String(row.company_id))
                                        .map(f => (
                                          <option key={f.id} value={f.id}>
                                            {f.fd_number} (Balance: ₹{f.total_amount})
                                          </option>
                                        ))}
                                      <option value="NEW">Create New FD Ledger...</option>
                                    </select>

                                    {(tempFDId[row.id] === null || String(tempFDId[row.id]) === 'NEW' || !tempFDId[row.id]) && (
                                      <input
                                        type="text"
                                        placeholder="Enter Fixed Deposit (FD) Number..."
                                        value={tempFDNumber[row.id] || ""}
                                        onChange={e => setTempFDNumber(prev => ({ ...prev, [row.id]: e.target.value }))}
                                        className="w-full border border-cyan-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500 bg-cyan-50/20 text-cyan-900"
                                      />
                                    )}
                                  </>
                                )}
                                <div className="flex gap-2 justify-end">
                                  {(row.mapped_invoice_id || row.mapped_loan_id || row.mapped_fd_id) && (
                                    <button
                                      onClick={() => {
                                        setEditMode(prev => ({ ...prev, [row.id]: false }));
                                        setTempRemarks(prev => {
                                          const updated = { ...prev };
                                          delete updated[row.id];
                                          return updated;
                                        });
                                      }}
                                      className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-[10px] font-bold uppercase transition"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                  <button
                                    onClick={async () => {
                                      const currentMode = mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : 'invoice');
                                      const remarksVal = tempRemarks[row.id] !== undefined ? tempRemarks[row.id] : (row.remarks || "");
 
                                      if (currentMode === 'advance') {
                                        const custId = tempCustomerId[row.id] !== undefined ? tempCustomerId[row.id] : row.mapped_customer_id;
                                        if (!custId) {
                                          alert("Please select a Customer for the advance payment.");
                                          return;
                                        }
                                        setTempMappingRows(prev => ({ ...prev, [row.id]: true }));
                                        await handleMapRow(
                                          row.id,
                                          null,
                                          null,
                                          null,
                                          undefined,
                                          null,
                                          undefined,
                                          remarksVal,
                                          undefined,
                                          undefined,
                                          null,
                                          undefined,
                                          null,
                                          null,
                                          null,
                                          undefined,
                                          undefined,
                                          Number(custId),
                                          null,
                                          true
                                        );
                                        setTempMappingRows(prev => ({ ...prev, [row.id]: false }));
                                        return;
                                      }
                                      if (currentMode === 'invoice') {
                                        const targetId = tempInvoice[row.id] !== undefined ? tempInvoice[row.id] : row.mapped_invoice_id;
                                        if (String(targetId) === 'NEW') {
                                          const compId = row.company_id || selectedUploadCompanyId || localStorage.getItem("selected_company_id");
                                          if (!compId) {
                                            alert("Please select a company to create the invoice.");
                                            return;
                                          }
                                          const custId = tempInvoiceCustomer[row.id];
                                          const invNo = tempInvoiceNo[row.id];
                                          if (!custId || !invNo) {
                                            alert("Please select a customer and enter an invoice number to create the new invoice.");
                                            return;
                                          }
                                          try {
                                            const invRes = await axios.post(`${API_URL}/auth/invoice`, {
                                              Company_id: Number(compId),
                                              Customer_id: Number(custId),
                                              InvoiceNo: invNo,
                                              Date: convertToISODate(row.date_str),
                                              Total_Amount: row.credit,
                                              Particulars: row.particulars,
                                              SGSTAmount: 0,
                                              CGSTAmount: 0,
                                              IGSTAmount: 0,
                                              remarks: remarksVal
                                            });
                                            const newInvoiceId = invRes.data.invoice_id;
                                            await handleMapRow(row.id, newInvoiceId, null, null, undefined, null, undefined, remarksVal);
                                            await fetchMappingTargets();
                                          } catch (err: any) {
                                            console.error("Error creating inline invoice:", err);
                                            alert(err?.response?.data?.error || "Failed to create inline invoice.");
                                            return;
                                          }
                                        } else {
                                          if (clubMode[row.id]) {
                                            const selectedIds = selectedClubInvoiceIds[row.id] || [];
                                            if (selectedIds.length === 0) {
                                              alert("Please select at least one invoice to club.");
                                              return;
                                            }
                                            setTempMappingRows(prev => ({ ...prev, [row.id]: true }));
                                            await handleMapRow(
                                              row.id,
                                              null,
                                              null,
                                              null,
                                              undefined,
                                              null,
                                              undefined,
                                              remarksVal,
                                              undefined,
                                              undefined,
                                              null,
                                              undefined,
                                              null,
                                              null,
                                              null,
                                              selectedIds,
                                              Number(gatewayFee[row.id] || 0)
                                            );
                                            setTempMappingRows(prev => ({ ...prev, [row.id]: false }));
                                            return;
                                          }
                                                                          const gatewayVal = Number(gatewayFee[row.id] || 0);
                                          const invoice = invoices.find(inv => inv.id === Number(targetId));
                                          if (invoice) {
                                            const alreadyPaid = Number(invoice.amount_paid || 0);
                                            const pendingAmount = invoice.Total_Amount - alreadyPaid;
                                            const currentTotalPayment = row.credit + gatewayVal;

                                            if (currentTotalPayment < pendingAmount) {
                                              setTdsInputVal("");
                                              setWarningModal({
                                                isOpen: true,
                                                type: 'credit',
                                                rowId: row.id,
                                                invoiceId: Number(targetId),
                                                purchaseId: null,
                                                remarks: remarksVal,
                                                invoiceNo: invoice.InvoiceNo,
                                                invoiceAmount: invoice.Total_Amount,
                                                pendingAmount: pendingAmount,
                                                receivedAmount: row.credit,
                                                gatewayFee: gatewayVal,
                                                difference: pendingAmount - currentTotalPayment,
                                                isExcess: false
                                              });
                                              return;
                                            } else if (currentTotalPayment > pendingAmount) {
                                              setWarningModal({
                                                isOpen: true,
                                                type: 'credit',
                                                rowId: row.id,
                                                invoiceId: Number(targetId),
                                                purchaseId: null,
                                                remarks: remarksVal,
                                                invoiceNo: invoice.InvoiceNo,
                                                invoiceAmount: invoice.Total_Amount,
                                                pendingAmount: pendingAmount,
                                                receivedAmount: row.credit,
                                                gatewayFee: gatewayVal,
                                                difference: currentTotalPayment - pendingAmount,
                                                isExcess: true
                                              });
                                              return;
                                            }
                                          }
                                          await handleMapRow(
                                            row.id,
                                            targetId ? Number(targetId) : null,
                                            null,
                                            null,
                                            undefined,
                                            null,
                                            undefined,
                                            remarksVal,
                                            undefined,
                                            false,
                                            null,
                                            undefined,
                                            null,
                                            null,
                                            null,
                                            undefined,
                                            gatewayVal
                                          );
                                        }
                                      } else if (currentMode === 'loan') {
                                        const selectVal = tempLoanId[row.id];
                                        const isNew = selectVal === null || String(selectVal) === 'NEW' || !selectVal;
                                        const targetLoanVal = isNew ? null : Number(selectVal);
                                        const nameVal = tempLenderName[row.id] || '';
 
                                        if (isNew && !nameVal.trim()) {
                                          alert("Please enter a lender name for the new loan.");
                                          return;
                                        }
                                        await handleMapRow(row.id, null, null, targetLoanVal, isNew ? nameVal : undefined, null, undefined, remarksVal);
                                      } else if (currentMode === 'fd') {
                                        const selectVal = tempFDId[row.id];
                                        const isNew = selectVal === null || String(selectVal) === 'NEW' || !selectVal;
                                        const targetFDVal = isNew ? null : Number(selectVal);
                                        const fdNum = tempFDNumber[row.id] || '';

                                        if (isNew && !fdNum.trim()) {
                                          alert("Please enter an FD Number for the new Fixed Deposit.");
                                          return;
                                        }
                                        await handleMapRow(row.id, null, null, null, undefined, null, undefined, remarksVal, undefined, undefined, targetFDVal, isNew ? fdNum : undefined);
                                      }
                                      setEditMode(prev => ({ ...prev, [row.id]: false }));
                                    }}
                                    className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-[10px] font-bold uppercase transition"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            )
                          ) : row.debit > 0 ? (
                            row.mapped_purchase_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-red-50 border-2 border-red-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-red-950 truncate max-w-[170px]" title={(() => {
                                  const pur = purchases.find(p => p.id === row.mapped_purchase_id);
                                  const targetPur: any = pur;
                                  const vendorObj = vendors.find(v => String(v.id) === String(targetPur?.Vendor_id));
                                  return `${pur?.InvoiceNo || ''}${vendorObj ? ` - ${vendorObj.company_name}` : ''} (₹${pur?.Total_Amount || 0})`;
                                })()}>
                                  Mapped: {(() => {
                                    const pur = purchases.find(p => p.id === row.mapped_purchase_id);
                                    const targetPur: any = pur;
                                    const vendorObj = vendors.find(v => String(v.id) === String(targetPur?.Vendor_id));
                                    return `${pur?.InvoiceNo || ''}${vendorObj ? ` - ${vendorObj.company_name}` : ''} (₹${pur?.Total_Amount || 0})`;
                                  })()}
                                </span>
                                {row.mapped_purchase_id && (() => {
                                  const pur = purchases.find(p => p.id === row.mapped_purchase_id);
                                  if (pur && pur.attachment_url) {
                                    const url = pur.attachment_url.startsWith('http') ? pur.attachment_url : `${API_URL}${pur.attachment_url}`;
                                    return (
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[10px] text-blue-600 font-bold underline block mt-0.5"
                                      >
                                        📄 View Bill Attachment
                                      </a>
                                    );
                                  }
                                  return null;
                                })()}
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'purchase' }));
                                    setTempPurchase(prev => ({ ...prev, [row.id]: row.mapped_purchase_id }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : row.mapped_loan_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-purple-50 border-2 border-purple-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-purple-950 truncate max-w-[170px]" title={(() => {
                                  const ln = loans.find(l => l.id === row.mapped_loan_id);
                                  return `Mapped Loan Repayment: ${ln?.lender_name || ''} (₹${row.debit})`;
                                })()}>
                                  Mapped Loan: {(() => {
                                    const ln = loans.find(l => l.id === row.mapped_loan_id);
                                    return ln?.lender_name || 'Loan';
                                  })()}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'loan' }));
                                    setTempLoanId(prev => ({ ...prev, [row.id]: row.mapped_loan_id }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : row.mapped_expense_head_id && !editMode[row.id] ? (
              <div className="flex items-center justify-between gap-2 p-1.5 bg-amber-50 border-2 border-amber-200 rounded-xl min-w-[250px]">
                <div className="flex flex-col gap-0.5 truncate max-w-[170px]">
                  <span className="text-xs font-bold text-amber-950 truncate" title={(() => {
                    const eh = expenseHeads.find(e => e.id === row.mapped_expense_head_id);
                    return `Mapped Expense: ${eh?.name || ''}`;
                  })()}>
                    Mapped Expense: {(() => {
                      const eh = expenseHeads.find(e => e.id === row.mapped_expense_head_id);
                      return eh?.name || 'Expense';
                    })()}
                  </span>
                  {row.gst_rate && (
                    <span className="text-[10px] text-amber-700 font-bold">
                      GST: {row.gst_rate}% (Inward: ₹{Number(row.gst_amount || 0).toFixed(2)})
                    </span>
                  )}
                  {row.expense_attachment_url && (
                    <a
                      href={row.expense_attachment_url.startsWith('http') ? row.expense_attachment_url : `${API_URL}${row.expense_attachment_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] text-cyan-600 font-bold underline block mt-0.5"
                    >
                      📄 View Invoice Attachment
                    </a>
                  )}
                </div>
                <button
                  onClick={() => {
                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                    setMappingMode(prev => ({ ...prev, [row.id]: 'expense' }));
                    setTempExpenseHeadId(prev => ({ ...prev, [row.id]: row.mapped_expense_head_id }));
                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                    setTempIsGstExpense(prev => ({ ...prev, [row.id]: !!row.gst_rate }));
                    setTempGstRate(prev => ({ ...prev, [row.id]: row.gst_rate || 18 }));
                    setTempTdsExpense(prev => ({ ...prev, [row.id]: row.tds_amount || "" }));
                    setTempVendorId(prev => ({ ...prev, [row.id]: row.mapped_vendor_id || null }));
                    setTempExpenseAttachment(prev => ({ ...prev, [row.id]: row.expense_attachment_url || null }));
                    const baseAmtForTds = Number(row.debit || 0) - Number(row.gst_amount || 0) + Number(row.tds_amount || 0);
                    const initTdsRate = baseAmtForTds > 0 ? (Number(row.tds_amount || 0) * 100) / baseAmtForTds : 10;
                    setTempTdsRateExpense(prev => ({ ...prev, [row.id]: Math.round(initTdsRate * 10) / 10 }));
                  }}
                  className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow flex-shrink-0"
                >
                  Edit
                </button>
              </div>
                            ) : row.mapped_fd_id && !editMode[row.id] ? (
                              <div className="flex items-center justify-between gap-2 p-1.5 bg-cyan-50 border-2 border-cyan-200 rounded-xl min-w-[250px]">
                                <span className="text-xs font-bold text-cyan-950 truncate max-w-[170px]" title={(() => {
                                  const fd = fixedDeposits.find(f => f.id === row.mapped_fd_id);
                                  return `Mapped FD Booked: ${fd?.fd_number || ''} (₹${row.debit})`;
                                })()}>
                                  Mapped FD: {(() => {
                                    const fd = fixedDeposits.find(f => f.id === row.mapped_fd_id);
                                    return fd?.fd_number || 'FD';
                                  })()}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditMode(prev => ({ ...prev, [row.id]: true }));
                                    setMappingMode(prev => ({ ...prev, [row.id]: 'fd' }));
                                    setTempFDId(prev => ({ ...prev, [row.id]: row.mapped_fd_id }));
                                    setTempRemarks(prev => ({ ...prev, [row.id]: row.remarks || "" }));
                                  }}
                                  className="px-2 py-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                                >
                                  Edit
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1.5 min-w-[200px]">
                                <select
                                  value={mappingMode[row.id] || (row.is_advance ? 'advance' : row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : row.mapped_expense_head_id ? 'expense' : 'purchase')}
                                  onChange={e => setMappingMode(prev => ({ ...prev, [row.id]: e.target.value as any }))}
                                  className="form-select border border-gray-300 rounded-lg p-1 text-xs bg-white text-gray-700 w-full"
                                >
                                  <option value="purchase">Map to Bill (Purchase/Expense)</option>
                                  <option value="loan">Map to Loan (Repayment)</option>
                                  <option value="expense">Direct Expense (Ledger Head)</option>
                                  <option value="fd">Map to Fixed Deposit (Booked)</option>
                                  <option value="advance">Vendor Advance Payment</option>
                                </select>
 
                                {(mappingMode[row.id] || (row.is_advance ? 'advance' : row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : row.mapped_expense_head_id ? 'expense' : 'purchase')) === 'advance' ? (
                                  <>
                                    <select
                                      value={tempVendorId[row.id] !== undefined ? (tempVendorId[row.id] || "") : (row.mapped_vendor_id || "")}
                                      onChange={e => setTempVendorId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border border-cyan-300 text-cyan-900 rounded-lg p-1.5 text-xs bg-cyan-50 font-bold w-full"
                                    >
                                      <option value="">Select Vendor for Advance...</option>
                                      {vendors
                                        .filter(v => !row.company_id || !v.company_id || String(v.company_id) === String(row.company_id))
                                        .map(v => (
                                          <option key={v.id} value={v.id}>{v.company_name}</option>
                                        ))}
                                    </select>
                                  </>
                                ) : (mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : row.mapped_expense_head_id ? 'expense' : 'purchase')) === 'purchase' ? (
                                  <>
                                    <input
                                      type="text"
                                      placeholder="Type to search Bill..."
                                      value={rowSearch[row.id] || ""}
                                      onChange={e => setRowSearch(prev => ({ ...prev, [row.id]: e.target.value }))}
                                      className="w-full border border-red-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-red-500 bg-red-50/20 text-red-900"
                                    />
                                    <select
                                      value={tempPurchase[row.id] !== undefined ? (tempPurchase[row.id] || "") : (row.mapped_purchase_id || "")}
                                      onChange={e => setTempPurchase(prev => ({ ...prev, [row.id]: e.target.value === 'NEW' ? 'NEW' : (e.target.value ? Number(e.target.value) : null) }))}
                                      className="form-select border-2 border-red-200 text-red-900 rounded-lg p-1.5 text-xs bg-red-50 font-bold w-full"
                                    >
                                      <option value="">Map to Bill (Purchase/Expense)</option>
                                      {purchases
                                        .filter((pur: Purchase) => {
                                          const targetPur: any = pur;
                                          const isSearched = pur.InvoiceNo.toLowerCase().includes((rowSearch[row.id] || "").toLowerCase());
                                          const isAlreadyMappedToOther = rows.some(r => r.mapped_purchase_id === pur.id && r.id !== row.id);
                                          const isSameCompany = !row.company_id || String(targetPur.Company_id) === String(row.company_id);
                                          return isSearched && !isAlreadyMappedToOther && isSameCompany;
                                        })
                                        .map((pur: Purchase) => {
                                          const targetPur: any = pur;
                                          const vendorObj = vendors.find(v => String(v.id) === String(targetPur.Vendor_id));
                                          const vendNameStr = vendorObj ? ` - ${vendorObj.company_name}` : '';
                                          return (
                                            <option key={pur.id} value={pur.id}>
                                              {pur.InvoiceNo}{vendNameStr} (₹{pur.Total_Amount})
                                            </option>
                                          );
                                        })}
                                      <option value="NEW">Create New Bill / Purchase...</option>
                                    </select>

                                    {String(tempPurchase[row.id]) === 'NEW' && (
                                      <div className="flex flex-col gap-1.5 mt-1 border-t pt-1 border-red-200">
                                        <select
                                          value={tempPurchaseVendor[row.id] || ""}
                                          onChange={e => setTempPurchaseVendor(prev => ({ ...prev, [row.id]: e.target.value }))}
                                          className="form-select border border-red-300 text-red-950 rounded p-1 text-xs bg-white w-full font-bold"
                                        >
                                          <option value="">Select Vendor *</option>
                                          {vendors
                                            .filter(v => !row.company_id || !v.company_id || String(v.company_id) === String(row.company_id))
                                            .map(v => (
                                              <option key={v.id} value={v.id}>{v.company_name}</option>
                                            ))}
                                        </select>
                                        <input
                                          type="text"
                                          placeholder="Bill Number *"
                                          value={tempPurchaseNo[row.id] || ""}
                                          onChange={e => setTempPurchaseNo(prev => ({ ...prev, [row.id]: e.target.value }))}
                                          className="w-full border border-red-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-red-500 bg-white text-red-950 font-bold"
                                        />
                                      </div>
                                    )}
                                  </>
                                ) : (mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : row.mapped_expense_head_id ? 'expense' : 'purchase')) === 'loan' ? (
                                  <>
                                    <select
                                      value={tempLoanId[row.id] !== undefined ? (tempLoanId[row.id] || "") : (row.mapped_loan_id || "")}
                                      onChange={e => setTempLoanId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border-2 border-purple-200 text-purple-900 rounded-lg p-1.5 text-xs bg-purple-50 font-bold w-full"
                                    >
                                      <option value="">Select Existing Lender...</option>
                                      {loans
                                        .filter(l => !row.company_id || String(l.company_id) === String(row.company_id))
                                        .map(l => (
                                          <option key={l.id} value={l.id}>
                                            {l.lender_name} (Balance: ₹{l.total_amount})
                                          </option>
                                        ))}
                                      <option value="NEW">Create New Lender / Person...</option>
                                    </select>
 
                                    {(tempLoanId[row.id] === null || String(tempLoanId[row.id]) === 'NEW' || !tempLoanId[row.id]) && (
                                      <input
                                        type="text"
                                        placeholder="Enter Lender / Person Name..."
                                        value={tempLenderName[row.id] || ""}
                                        onChange={e => setTempLenderName(prev => ({ ...prev, [row.id]: e.target.value }))}
                                        className="w-full border border-purple-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-purple-50/20 text-purple-900"
                                      />
                                    )}
                                  </>
                                ) : (mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : row.mapped_expense_head_id ? 'expense' : 'purchase')) === 'expense' ? (
                                  <>
                                    <select
                                      value={tempExpenseHeadId[row.id] !== undefined ? (tempExpenseHeadId[row.id] || "") : (row.mapped_expense_head_id || "")}
                                      onChange={e => setTempExpenseHeadId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border-2 border-amber-200 text-amber-900 rounded-lg p-1.5 text-xs bg-amber-50 font-bold w-full"
                                    >
                                      <option value="">Select Existing Head...</option>
                                      {expenseHeads
                                        .filter(eh => !row.company_id || String(eh.company_id) === String(row.company_id))
                                        .map(eh => (
                                          <option key={eh.id} value={eh.id}>
                                            {eh.name}
                                          </option>
                                        ))}
                                      <option value="NEW">Create New Ledger Head...</option>
                                    </select>
 
                                    {(tempExpenseHeadId[row.id] === null || String(tempExpenseHeadId[row.id]) === 'NEW' || !tempExpenseHeadId[row.id]) && (
                                      <input
                                        type="text"
                                        placeholder="Enter Expense Head Name (e.g. Rent)..."
                                        value={tempExpenseHeadName[row.id] || ""}
                                        onChange={e => setTempExpenseHeadName(prev => ({ ...prev, [row.id]: e.target.value }))}
                                        className="w-full border border-amber-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500 bg-amber-50/20 text-amber-900"
                                      />
                                    )}

                                    {/* GST % Inward GST estimation fields */}
                                    <div className="mt-2 p-2 bg-amber-50/40 border border-amber-200 rounded-lg flex flex-col gap-1.5 text-left">
                                      <label className="flex items-center gap-1.5 text-xs text-amber-950 font-semibold cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={tempIsGstExpense[row.id] || false}
                                          onChange={e => {
                                            const checked = e.target.checked;
                                            setTempIsGstExpense(prev => ({ ...prev, [row.id]: checked }));
                                            if (checked && tempGstRate[row.id] === undefined) {
                                              setTempGstRate(prev => ({ ...prev, [row.id]: 18 }));
                                            }
                                          }}
                                          className="form-checkbox h-3.5 w-3.5 text-amber-600 rounded"
                                        />
                                        <span>GST Invoice Expense</span>
                                      </label>

                                      {(tempIsGstExpense[row.id]) && (
                                        <div className="flex flex-col gap-1 mt-1">
                                          <div className="flex gap-2 items-center">
                                            <span className="text-[10px] text-amber-900 font-bold uppercase w-12">GST %:</span>
                                            <select
                                              value={tempGstRate[row.id] !== undefined && tempGstRate[row.id] !== null ? String(tempGstRate[row.id]) : "18"}
                                              onChange={e => setTempGstRate(prev => ({ ...prev, [row.id]: Number(e.target.value) }))}
                                              className="form-select border border-amber-300 rounded p-1 text-[10px] bg-white font-bold text-amber-900 flex-grow"
                                            >
                                              <option value="5">5%</option>
                                              <option value="12">12%</option>
                                              <option value="18">18%</option>
                                              <option value="28">28%</option>
                                            </select>
                                          </div>
                                          <div className="flex gap-2 items-center">
                                            <span className="text-[10px] text-amber-900 font-bold uppercase w-12">TDS %:</span>
                                            <input
                                              type="number"
                                              placeholder="TDS % (e.g. 10)..."
                                              value={tempTdsRateExpense[row.id] !== undefined ? tempTdsRateExpense[row.id] : "10"}
                                              onChange={e => setTempTdsRateExpense(prev => ({ ...prev, [row.id]: e.target.value }))}
                                              className="w-full border border-amber-300 rounded p-1 text-[10px] bg-white font-bold text-amber-900 flex-grow"
                                            />
                                          </div>
                                          <div className="flex gap-2 items-center">
                                            <span className="text-[10px] text-amber-900 font-bold uppercase w-12">Invoice:</span>
                                            <div className="flex flex-col gap-1 flex-grow">
                                              <input
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={async (e) => {
                                                  const file = e.target.files?.[0];
                                                  if (!file) return;
                                                  const formData = new FormData();
                                                  formData.append('file', file);
                                                  try {
                                                    const res = await axios.post(`${API_URL}/auth/upload-attachment`, formData, {
                                                      headers: { 'Content-Type': 'multipart/form-data' }
                                                    });
                                                    setTempExpenseAttachment(prev => ({ ...prev, [row.id]: res.data.file_url }));
                                                    alert("Invoice uploaded successfully!");
                                                  } catch (err) {
                                                    alert("Failed to upload invoice.");
                                                  }
                                                }}
                                                className="w-full border border-amber-300 rounded p-0.5 text-[10px] bg-white font-bold text-amber-900"
                                              />
                                              {(tempExpenseAttachment[row.id] || row.expense_attachment_url) && (
                                                <a
                                                  href={(tempExpenseAttachment[row.id] || row.expense_attachment_url || '').startsWith('http') ? (tempExpenseAttachment[row.id] || row.expense_attachment_url || '') : `${API_URL}${tempExpenseAttachment[row.id] || row.expense_attachment_url}`}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="text-[9px] text-blue-600 font-bold underline"
                                                >
                                                  View Current Invoice
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex gap-2 items-center">
                                            <span className="text-[10px] text-amber-900 font-bold uppercase w-12">Vendor:</span>
                                            <select
                                              value={tempVendorId[row.id] !== undefined ? (tempVendorId[row.id] || "") : (row.mapped_vendor_id || "")}
                                              onChange={e => setTempVendorId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                              className="form-select border border-amber-300 rounded p-1 text-[10px] bg-white font-bold text-amber-900 flex-grow w-full"
                                            >
                                              <option value="">Select Vendor...</option>
                                              {vendors
                                                .filter(v => !row.company_id || !v.company_id || String(v.company_id) === String(row.company_id))
                                                .map(v => (
                                                  <option key={v.id} value={v.id}>{v.company_name}</option>
                                                ))}
                                            </select>
                                          </div>
                                          {(() => {
                                            const rate = tempGstRate[row.id] !== undefined && tempGstRate[row.id] !== null ? Number(tempGstRate[row.id]) : 18;
                                            const tdsRate = tempTdsRateExpense[row.id] !== undefined && tempTdsRateExpense[row.id] !== "" ? Number(tempTdsRateExpense[row.id]) : 10;
                                            const total = row.debit || 0;
                                            const baseAmt = total / (1 + (rate - tdsRate) / 100);
                                            const gstAmt = baseAmt * (rate / 100);
                                            const tdsAmt = baseAmt * (tdsRate / 100);
                                            return (
                                              <div className="text-[10px] text-amber-900 bg-white/80 p-1.5 rounded border border-amber-100 flex flex-col gap-0.5">
                                                <div className="flex justify-between">
                                                  <span>Base Amount:</span>
                                                  <span className="font-bold">₹{baseAmt.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between text-amber-700 font-medium">
                                                  <span>Est. Inward GST ({rate}%):</span>
                                                  <span className="font-bold">₹{gstAmt.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between text-red-700 font-medium">
                                                  <span>Calculated TDS ({tdsRate}%):</span>
                                                  <span className="font-bold">₹{tdsAmt.toFixed(2)}</span>
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <select
                                      value={tempFDId[row.id] !== undefined ? (tempFDId[row.id] || "") : (row.mapped_fd_id || "")}
                                      onChange={e => setTempFDId(prev => ({ ...prev, [row.id]: e.target.value ? Number(e.target.value) : null }))}
                                      className="form-select border-2 border-cyan-200 text-cyan-900 rounded-lg p-1.5 text-xs bg-cyan-50 font-bold w-full"
                                    >
                                      <option value="">Select Existing FD...</option>
                                      {fixedDeposits
                                        .filter(f => !row.company_id || String(f.company_id) === String(row.company_id))
                                        .map(f => (
                                          <option key={f.id} value={f.id}>
                                            {f.fd_number} (Balance: ₹{f.total_amount})
                                          </option>
                                        ))}
                                      <option value="NEW">Create New FD Ledger...</option>
                                    </select>
 
                                    {(tempFDId[row.id] === null || String(tempFDId[row.id]) === 'NEW' || !tempFDId[row.id]) && (
                                      <input
                                        type="text"
                                        placeholder="Enter Fixed Deposit (FD) Number..."
                                        value={tempFDNumber[row.id] || ""}
                                        onChange={e => setTempFDNumber(prev => ({ ...prev, [row.id]: e.target.value }))}
                                        className="w-full border border-cyan-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500 bg-cyan-50/20 text-cyan-900"
                                      />
                                    )}
                                  </>
                                )}
 
                                <div className="flex gap-2 justify-end">
                                  {(row.mapped_purchase_id || row.mapped_loan_id || row.mapped_expense_head_id || row.mapped_fd_id) && (
                                    <button
                                      onClick={() => {
                                        setEditMode(prev => ({ ...prev, [row.id]: false }));
                                        setTempRemarks(prev => {
                                          const updated = { ...prev };
                                          delete updated[row.id];
                                          return updated;
                                        });
                                      }}
                                      className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-[10px] font-bold uppercase transition"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                  <button
                                    onClick={async () => {
                                      const currentMode = mappingMode[row.id] || (row.mapped_fd_id ? 'fd' : row.mapped_loan_id ? 'loan' : row.mapped_expense_head_id ? 'expense' : 'purchase');
                                      const remarksVal = tempRemarks[row.id] !== undefined ? tempRemarks[row.id] : (row.remarks || "");
 
                                      if (currentMode === 'advance') {
                                        const vendId = tempVendorId[row.id] !== undefined ? tempVendorId[row.id] : row.mapped_vendor_id;
                                        if (!vendId) {
                                          alert("Please select a Vendor for the advance payment.");
                                          return;
                                        }
                                        setTempMappingRows(prev => ({ ...prev, [row.id]: true }));
                                        await handleMapRow(
                                          row.id,
                                          null,
                                          null,
                                          null,
                                          undefined,
                                          null,
                                          undefined,
                                          remarksVal,
                                          undefined,
                                          undefined,
                                          null,
                                          undefined,
                                          null,
                                          null,
                                          null,
                                          undefined,
                                          undefined,
                                          null,
                                          Number(vendId),
                                          true
                                        );
                                        setTempMappingRows(prev => ({ ...prev, [row.id]: false }));
                                        return;
                                      }
                                      if (currentMode === 'purchase') {
                                        const targetId = tempPurchase[row.id] !== undefined ? tempPurchase[row.id] : row.mapped_purchase_id;
                                        if (String(targetId) === 'NEW') {
                                          const compId = row.company_id || selectedUploadCompanyId || localStorage.getItem("selected_company_id");
                                          if (!compId) {
                                            alert("Please select a company to create the purchase invoice.");
                                            return;
                                          }
                                          const vendId = tempPurchaseVendor[row.id];
                                          const billNo = tempPurchaseNo[row.id];
                                          if (!vendId || !billNo) {
                                            alert("Please select a vendor and enter a bill number to create the new purchase invoice.");
                                            return;
                                          }
                                          try {
                                            const purRes = await axios.post(`${API_URL}/auth/purchase-invoice`, {
                                              Company_id: Number(compId),
                                              Vendor_id: Number(vendId),
                                              InvoiceNo: billNo,
                                              Date: convertToISODate(row.date_str),
                                              Total_Amount: row.debit,
                                              Particulars: row.particulars,
                                              SGSTAmount: 0,
                                              CGSTAmount: 0,
                                              IGSTAmount: 0,
                                              remarks: remarksVal
                                            });
                                            const newPurchaseId = purRes.data.invoice_id;
                                            await handleMapRow(row.id, null, newPurchaseId, null, undefined, null, undefined, remarksVal);
                                            await fetchMappingTargets();
                                          } catch (err: any) {
                                            console.error("Error creating inline purchase invoice:", err);
                                            alert(err?.response?.data?.error || "Failed to create inline purchase invoice.");
                                            return;
                                          }
                                        } else {
                                          const purchase = purchases.find(pur => pur.id === Number(targetId));
                                          if (purchase) {
                                            const alreadyPaid = Number(purchase.amount_paid || 0);
                                            const pendingAmount = purchase.Total_Amount - alreadyPaid;
                                            const currentTotalPayment = row.debit;

                                            if (currentTotalPayment < pendingAmount) {
                                              setWarningModal({
                                                isOpen: true,
                                                type: 'debit',
                                                rowId: row.id,
                                                invoiceId: null,
                                                purchaseId: Number(targetId),
                                                remarks: remarksVal,
                                                invoiceNo: purchase.InvoiceNo,
                                                invoiceAmount: purchase.Total_Amount,
                                                pendingAmount: pendingAmount,
                                                receivedAmount: row.debit,
                                                difference: pendingAmount - currentTotalPayment,
                                                isExcess: false
                                              });
                                              return;
                                            } else if (currentTotalPayment > pendingAmount) {
                                              setWarningModal({
                                                isOpen: true,
                                                type: 'debit',
                                                rowId: row.id,
                                                invoiceId: null,
                                                purchaseId: Number(targetId),
                                                remarks: remarksVal,
                                                invoiceNo: purchase.InvoiceNo,
                                                invoiceAmount: purchase.Total_Amount,
                                                pendingAmount: pendingAmount,
                                                receivedAmount: row.debit,
                                                difference: currentTotalPayment - pendingAmount,
                                                isExcess: true
                                              });
                                              return;
                                            }
                                          }
                                          await handleMapRow(row.id, null, targetId ? Number(targetId) : null, null, undefined, null, undefined, remarksVal);
                                        }
                                      } else if (currentMode === 'loan') {
                                        const selectVal = tempLoanId[row.id];
                                        const isNew = selectVal === null || String(selectVal) === 'NEW' || !selectVal;
                                        const targetLoanVal = isNew ? null : Number(selectVal);
                                        const nameVal = tempLenderName[row.id] || '';
 
                                        if (isNew && !nameVal.trim()) {
                                          alert("Please enter a lender name for the new loan.");
                                          return;
                                        }
                                        await handleMapRow(row.id, null, null, targetLoanVal, isNew ? nameVal : undefined, null, undefined, remarksVal);
                                      } else if (currentMode === 'expense') {
                                         // Expense mapping mode
                                         const selectVal = tempExpenseHeadId[row.id];
                                         const isNew = selectVal === null || String(selectVal) === 'NEW' || !selectVal;
                                         const targetHeadId = isNew ? null : Number(selectVal);
                                         const headName = tempExpenseHeadName[row.id] || '';
 
                                         if (isNew && !headName.trim()) {
                                           alert("Please enter a name for the new Expense Head.");
                                           return;
                                         }
                                         const isGst = tempIsGstExpense[row.id] || false;
                                         const gstRateVal = isGst ? (tempGstRate[row.id] !== undefined && tempGstRate[row.id] !== null ? Number(tempGstRate[row.id]) : 18) : null;
                                         const tdsRateVal = isGst ? (tempTdsRateExpense[row.id] !== undefined && tempTdsRateExpense[row.id] !== "" ? Number(tempTdsRateExpense[row.id]) : 10) : 0;
                                         
                                         let gstAmtVal = null;
                                         let tdsAmtVal = undefined;
                                         if (isGst && gstRateVal !== null) {
                                           const baseAmt = row.debit / (1 + (gstRateVal - tdsRateVal) / 100);
                                           gstAmtVal = baseAmt * (gstRateVal / 100);
                                           tdsAmtVal = baseAmt * (tdsRateVal / 100);
                                         }
                                         
                                         const vendorIdVal = tempVendorId[row.id] !== undefined ? tempVendorId[row.id] : (row.mapped_vendor_id || null);
                                         const attachmentUrl = tempExpenseAttachment[row.id] !== undefined ? tempExpenseAttachment[row.id] : (row.expense_attachment_url || null);
                                         await handleMapRow(row.id, null, null, null, undefined, targetHeadId, isNew ? headName : undefined, remarksVal, tdsAmtVal, undefined, null, undefined, gstRateVal, gstAmtVal, attachmentUrl, undefined, undefined, null, vendorIdVal);
                                      } else if (currentMode === 'fd') {
                                        const selectVal = tempFDId[row.id];
                                        const isNew = selectVal === null || String(selectVal) === 'NEW' || !selectVal;
                                        const targetFDVal = isNew ? null : Number(selectVal);
                                        const fdNum = tempFDNumber[row.id] || '';
 
                                        if (isNew && !fdNum.trim()) {
                                          alert("Please enter an FD Number for the new Fixed Deposit.");
                                          return;
                                        }
                                        await handleMapRow(row.id, null, null, null, undefined, null, undefined, remarksVal, undefined, undefined, targetFDVal, isNew ? fdNum : undefined);
                                      }
                                      setEditMode(prev => ({ ...prev, [row.id]: false }));
                                    }}
                                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[10px] font-bold uppercase transition"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            )
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <button
                            onClick={() => handleDeleteStatementRow(row.id)}
                            className="p-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition"
                            title="Delete transaction entry"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Loans Ledger View */}
      {activeTab === 'ledger' && (
        <div className="bg-white shadow-xl rounded-2xl border border-purple-200 overflow-hidden p-6 mb-8">
          <div className="mb-6 flex justify-between items-center border-b border-purple-50 pb-4">
            <div>
              <h3 className="text-xl font-extrabold text-purple-900">Loans & Borrowings Ledger</h3>
              <p className="text-gray-500 text-xs mt-1">Track funds borrowed from individuals/lenders and repayments made. Aggregated from statement mappings.</p>
            </div>
            <span className="text-xs bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-bold">
              {loans.length} active ledgers
            </span>
          </div>

          {loans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl block mb-2">🤝</span>
              <p className="font-semibold text-sm">No loan entries mapped yet.</p>
              <p className="text-xs text-gray-400 mt-1">Go to "Statement Reconciliation" and map credit/debit transactions to a Loan mapping.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {loans.map((loan) => {
                const isExpanded = !!expandedLoans[loan.id];
                const entries = loanEntries[loan.id] || [];
                return (
                  <div key={loan.id} className="border border-purple-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition bg-white">
                    {/* Header Row */}
                    <div 
                      onClick={() => toggleLoanExpand(loan.id)}
                      className="bg-purple-50/20 p-4 flex justify-between items-center cursor-pointer hover:bg-purple-50/50 transition"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-lg font-bold">
                          {loan.lender_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="font-bold text-blue-950">{loan.lender_name}</h4>
                          <span className="text-[10px] text-gray-400 uppercase font-semibold">Lender Ledger</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className="text-xs text-gray-400 block font-medium">Outstanding Balance</span>
                          <span className={`font-extrabold text-sm ${loan.total_amount >= 0 ? 'text-purple-700' : 'text-green-600'}`}>
                            ₹{loan.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <span className="text-purple-400 text-xs font-semibold">
                          {isExpanded ? '▲ Close Details' : '▼ View History'}
                        </span>
                      </div>
                    </div>

                    {/* Expandable Transaction History */}
                    {isExpanded && (
                      <div className="border-t border-purple-100 bg-purple-50/5 p-4">
                        <h5 className="text-xs font-bold text-gray-400 uppercase mb-3 tracking-wider">Transaction History</h5>
                        {entries.length === 0 ? (
                          <p className="text-xs text-gray-500 italic py-2">No transactions recorded for this ledger.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="border-b border-gray-200 text-gray-400 font-bold uppercase text-[10px] tracking-wider">
                                  <th className="py-2">Date</th>
                                  <th className="py-2">Particulars</th>
                                  <th className="py-2">Remarks</th>
                                  <th className="py-2 text-right">Debit / Repayment (₹)</th>
                                  <th className="py-2 text-right">Credit / Borrowed (₹)</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {entries.map((entry) => (
                                  <tr key={entry.id} className="hover:bg-purple-50/10 transition-colors">
                                    <td className="py-2.5 font-medium text-gray-700">{entry.date_str}</td>
                                    <td className="py-2.5 text-gray-600 max-w-xs truncate" title={entry.particulars}>{entry.particulars}</td>
                                    <td className="py-2.5 text-gray-500 italic max-w-xs truncate" title={entry.remarks || ''}>{entry.remarks || '—'}</td>
                                    <td className="py-2.5 text-right font-semibold text-red-600">
                                      {entry.debit > 0 ? `₹${entry.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                                    </td>
                                    <td className="py-2.5 text-right font-semibold text-green-600">
                                      {entry.credit > 0 ? `₹${entry.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Expense Heads Tab View */}
      {activeTab === 'expenses' && (
        <div className="bg-white shadow-xl rounded-2xl border border-blue-200 overflow-hidden p-6 mb-8">
          <div className="mb-6 flex justify-between items-center border-b border-blue-50 pb-4">
            <div>
              <h3 className="text-xl font-extrabold text-blue-900">Expense Heads (Ledgers)</h3>
              <p className="text-gray-500 text-xs mt-1">Manage categories used to track direct expenditures and debit transactions.</p>
            </div>
            <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
              {expenseHeads.length} expense heads
            </span>
          </div>

          {/* Add Form */}
          <form onSubmit={handleAddExpenseHead} className="bg-blue-50/30 border border-blue-100 rounded-2xl p-4 mb-6 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Expense Head Name *</label>
              <input
                type="text"
                placeholder="e.g. Office Rent, Server Hosting, Salary"
                value={newExpenseName}
                onChange={e => setNewExpenseName(e.target.value)}
                className="w-full border-2 border-blue-100 rounded-xl bg-white p-2 text-sm font-semibold text-blue-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            {companies.length > 1 && (
              <div className="min-w-[200px]">
                <label className="block text-gray-700 text-xs font-bold mb-1.5 uppercase text-blue-900/60 font-semibold">Assign to Company (Optional)</label>
                <select
                  value={newExpenseCompanyId}
                  onChange={e => setNewExpenseCompanyId(e.target.value)}
                  className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2 text-sm font-semibold text-blue-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Global (All Companies)</option>
                  {companies.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="submit"
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-extrabold uppercase transition shadow-md"
            >
              ➕ Add Head
            </button>
          </form>

          {/* Table List */}
          {expenseHeads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl block mb-2">🏷️</span>
              <p className="font-semibold text-sm">No expense heads found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-400 font-bold uppercase text-xs tracking-wider">
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Associated Company</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {expenseHeads.map((eh) => {
                    const comp = companies.find(c => String(c.id) === String(eh.company_id));
                    const isEditing = editingExpenseId === eh.id;
                    return (
                      <tr key={eh.id} className="hover:bg-blue-50/5 transition">
                        <td className="py-3 px-4 font-semibold text-blue-950">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editingExpenseName}
                              onChange={e => setEditingExpenseName(e.target.value)}
                              className="border border-blue-300 rounded px-2.5 py-1 text-sm bg-white text-blue-955 font-semibold"
                            />
                          ) : (
                            eh.name
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600 font-medium">
                          {comp ? comp.company_name : <span className="text-gray-400 italic">Global (All Companies)</span>}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {isEditing ? (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => handleEditExpenseHead(eh.id)}
                                className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold uppercase transition"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingExpenseId(null);
                                  setEditingExpenseName("");
                                }}
                                className="px-2.5 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-xs font-bold uppercase transition"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => {
                                  setEditingExpenseId(eh.id);
                                  setEditingExpenseName(eh.name);
                                }}
                                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold uppercase transition shadow-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteExpenseHead(eh.id)}
                                className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold uppercase transition shadow-sm"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Statement History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white shadow-xl border border-blue-200 rounded-2xl p-6">
          <h3 className="font-bold text-blue-900 mb-4 text-lg">📂 Uploaded Statements</h3>
          {uploadedStatements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl block mb-2">📄</span>
              <p className="font-semibold text-sm">No uploaded statements found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-400 font-bold uppercase text-xs tracking-wider">
                    <th className="py-3 px-4">File Name</th>
                    <th className="py-3 px-4">Company</th>
                    <th className="py-3 px-4">Bank Account</th>
                    <th className="py-3 px-4">Uploaded At</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {uploadedStatements.map((stmt) => (
                    <tr key={stmt.id} className="hover:bg-blue-50/5 transition">
                      <td className="py-3 px-4 font-semibold text-blue-950 truncate max-w-[200px]" title={stmt.file_name}>
                        {stmt.file_name}
                      </td>
                      <td className="py-3 px-4 text-gray-600 font-medium">
                        {stmt.company_name || <span className="text-gray-400 italic">N/A</span>}
                      </td>
                      <td className="py-3 px-4 text-gray-600 font-medium">
                        {stmt.bank_name ? `${stmt.bank_name} (${stmt.account_number})` : <span className="text-gray-400 italic">N/A</span>}
                      </td>
                      <td className="py-3 px-4 text-gray-500">
                        {new Date(stmt.uploaded_at).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => handleDeleteStatement(stmt.id)}
                          className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold uppercase transition shadow-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Deleted Data (Trash) Tab */}
      {activeTab === 'trash' && (
        <div className="bg-white shadow-xl border border-blue-200 rounded-2xl p-6">
          <div className="mb-6 flex justify-between items-center border-b border-blue-50 pb-4">
            <div>
              <h3 className="text-xl font-extrabold text-blue-900">🗑️ Deleted Data (Trash Bin)</h3>
              <p className="text-gray-500 text-xs mt-1">View soft-deleted transactions and retrieve/restore them back to active reconciliation.</p>
            </div>
            <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
              {deletedRows.length} deleted rows
            </span>
          </div>

          {deletedLoading ? (
            <div className="text-center py-12 text-gray-500 font-semibold">Loading trash bin...</div>
          ) : deletedRows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl block mb-2">🗑️</span>
              <p className="font-semibold text-sm">Trash is empty. No deleted transactions.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse table-fixed text-[11px]">
                <thead>
                  <tr className="bg-gray-50 border-b text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    <th className="p-2 w-[12%]">Company</th>
                    <th className="p-2 w-[15%]">Bank Account</th>
                    <th className="p-2 w-[8%]">Date</th>
                    <th className="p-2 w-[30%]">Particulars</th>
                    <th className="p-2 text-right w-[10%]">Debit (₹)</th>
                    <th className="p-2 text-right w-[10%]">Credit (₹)</th>
                    <th className="p-2 text-center w-[15%]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deletedRows.map((row: any) => (
                    <tr key={row.id} className="hover:bg-blue-50/10 transition-colors">
                      <td className="p-2 truncate font-bold text-blue-900" title={row.company_name || 'Global'}>{row.company_name || <span className="text-gray-400 italic">Global</span>}</td>
                      <td className="p-2 truncate font-medium text-gray-700">
                        {row.bank_name ? `${row.bank_name} (${row.account_number})` : <span className="text-gray-400 italic">—</span>}
                      </td>
                      <td className="p-2 whitespace-nowrap font-medium text-gray-700">{row.date_str}</td>
                      <td className="p-2 text-gray-600 truncate" title={row.particulars}>{row.particulars}</td>
                      <td className="p-2 text-right font-medium text-red-600">
                        {row.debit > 0 ? `₹${row.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "—"}
                      </td>
                      <td className="p-2 text-right font-medium text-green-600">
                        {row.credit > 0 ? `₹${row.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "—"}
                      </td>
                      <td className="p-2 text-center">
                        <button
                          onClick={() => handleRestoreRow(row.id)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-[10px] font-extrabold uppercase transition-all duration-150 shadow"
                        >
                          ↩️ Retrieve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Universal Column Mapping Modal */}
      {previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs transition-all overflow-y-auto">
          <div className="bg-white rounded-3xl p-7 max-w-4xl w-full shadow-2xl border border-blue-100 transform scale-100 transition-all my-8 mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-extrabold text-blue-900">Universal Bank Statement Column Mapper</h4>
              {!isHighConfidence && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  ⚠️ Custom Mapping Required
                </span>
              )}
            </div>

            <p className="text-xs text-gray-500 mb-4">
              Specify which columns correspond to the statement data fields. We have pre-filled our best guesses.
            </p>

            {/* Config Mapping Dropdowns */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Header Row Index</label>
                <select
                  value={customMapping.headerRowIndex}
                  onChange={e => {
                    const rowIdx = Number(e.target.value);
                    const selectedHeaders = previewRows[rowIdx] || [];
                    setCustomMapping((prev: any) => ({
                      ...prev,
                      headerRowIndex: rowIdx,
                      // reset others as indices might shift
                      dateIdx: selectedHeaders.findIndex(h => String(h).toLowerCase().includes('date')),
                      particularsIdx: selectedHeaders.findIndex(h => String(h).toLowerCase().includes('particular') || String(h).toLowerCase().includes('narrat') || String(h).toLowerCase().includes('desc')),
                      debitIdx: selectedHeaders.findIndex(h => String(h).toLowerCase().includes('debit') || String(h).toLowerCase().includes('withdraw')),
                      creditIdx: selectedHeaders.findIndex(h => String(h).toLowerCase().includes('credit') || String(h).toLowerCase().includes('deposit')),
                      balanceIdx: selectedHeaders.findIndex(h => String(h).toLowerCase().includes('bal')),
                      chequeIdx: selectedHeaders.findIndex(h => String(h).toLowerCase().includes('chq') || String(h).toLowerCase().includes('ref'))
                    }));
                  }}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  {previewRows.slice(0, 10).map((r, idx) => (
                    <option key={idx} value={idx}>Row {idx + 1}: {r.slice(0, 4).join(', ') || '(Empty)'}...</option>
                  ))}
                </select>
              </div>

              {/* Date Column */}
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Date Column *</label>
                <select
                  value={customMapping.dateIdx}
                  onChange={e => setCustomMapping((prev: any) => ({ ...prev, dateIdx: Number(e.target.value) }))}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  <option value="-1">Select Column</option>
                  {(previewRows[customMapping.headerRowIndex] || []).map((col, idx) => (
                    <option key={idx} value={idx}>Col {idx + 1}: {String(col || `Column ${idx + 1}`)}</option>
                  ))}
                </select>
              </div>

              {/* Particulars Column */}
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Particulars *</label>
                <select
                  value={customMapping.particularsIdx}
                  onChange={e => setCustomMapping((prev: any) => ({ ...prev, particularsIdx: Number(e.target.value) }))}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  <option value="-1">Select Column</option>
                  {(previewRows[customMapping.headerRowIndex] || []).map((col, idx) => (
                    <option key={idx} value={idx}>Col {idx + 1}: {String(col || `Column ${idx + 1}`)}</option>
                  ))}
                </select>
              </div>

              {/* Cheque/Ref Column */}
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Cheque / Ref No.</label>
                <select
                  value={customMapping.chequeIdx}
                  onChange={e => setCustomMapping((prev: any) => ({ ...prev, chequeIdx: Number(e.target.value) }))}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  <option value="-1">None</option>
                  {(previewRows[customMapping.headerRowIndex] || []).map((col, idx) => (
                    <option key={idx} value={idx}>Col {idx + 1}: {String(col || `Column ${idx + 1}`)}</option>
                  ))}
                </select>
              </div>

              {/* Debit Column */}
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Debit (Withdrawal) *</label>
                <select
                  value={customMapping.debitIdx}
                  onChange={e => setCustomMapping((prev: any) => ({ ...prev, debitIdx: Number(e.target.value) }))}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  <option value="-1">Select Column</option>
                  {(previewRows[customMapping.headerRowIndex] || []).map((col, idx) => (
                    <option key={idx} value={idx}>Col {idx + 1}: {String(col || `Column ${idx + 1}`)}</option>
                  ))}
                </select>
              </div>

              {/* Credit Column */}
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Credit (Deposit) *</label>
                <select
                  value={customMapping.creditIdx}
                  onChange={e => setCustomMapping((prev: any) => ({ ...prev, creditIdx: Number(e.target.value) }))}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  <option value="-1">Select Column</option>
                  {(previewRows[customMapping.headerRowIndex] || []).map((col, idx) => (
                    <option key={idx} value={idx}>Col {idx + 1}: {String(col || `Column ${idx + 1}`)}</option>
                  ))}
                </select>
              </div>

              {/* Balance Column */}
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-1 uppercase">Running Balance</label>
                <select
                  value={customMapping.balanceIdx}
                  onChange={e => setCustomMapping((prev: any) => ({ ...prev, balanceIdx: Number(e.target.value) }))}
                  className="form-select w-full border border-gray-300 rounded p-1 text-xs bg-white font-semibold text-slate-800"
                >
                  <option value="-1">None</option>
                  {(previewRows[customMapping.headerRowIndex] || []).map((col, idx) => (
                    <option key={idx} value={idx}>Col {idx + 1}: {String(col || `Column ${idx + 1}`)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Preview Sheet Data */}
            <h5 className="font-bold text-slate-700 text-xs mb-2 uppercase">Sheet Data Preview (First 20 Rows)</h5>
            <div className="overflow-auto border rounded-xl max-h-[30vh] mb-6 text-[10px]">
              <table className="min-w-full text-left border-collapse font-mono">
                <tbody>
                  {previewRows.map((r, rIdx) => (
                    <tr
                      key={rIdx}
                      className={
                        rIdx === customMapping.headerRowIndex
                          ? 'bg-blue-100 border-y-2 border-blue-400 font-bold text-blue-950'
                          : rIdx % 2 === 0
                          ? 'bg-white'
                          : 'bg-slate-50'
                      }
                    >
                      <td className="p-1 text-slate-400 font-bold border-r text-center w-10">{rIdx + 1}</td>
                      {r.map((c, cIdx) => {
                        let isHighlighted = false;
                        if (rIdx > customMapping.headerRowIndex) {
                          isHighlighted = [customMapping.dateIdx, customMapping.particularsIdx, customMapping.debitIdx, customMapping.creditIdx].includes(cIdx);
                        }
                        return (
                          <td key={cIdx} className={`p-1 border-r border-b truncate max-w-[150px] ${isHighlighted ? 'bg-amber-50 font-medium text-slate-900' : 'text-slate-600'}`}>
                            {String(c)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setPreviewModalOpen(false); setPendingUploadFile(null); }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition text-xs uppercase"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={customMapping.dateIdx === -1 || customMapping.particularsIdx === -1 || customMapping.debitIdx === -1 || customMapping.creditIdx === -1}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-xl font-bold transition text-xs uppercase shadow-md flex items-center gap-1.5"
              >
                Confirm & Import Statement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation Warning Modal */}
      {/* Reconciliation Warning Modal */}
      {/* Reconciliation Warning Modal */}
      {warningModal && warningModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs transition-all">
          <div className="bg-white rounded-3xl p-7 max-w-md w-full shadow-2xl border border-blue-100 transform scale-100 transition-all">
            {warningModal.isExcess ? (
              <>
                <div className="flex items-center gap-3 text-red-600 mb-3">
                  <span className="text-2xl" role="img" aria-label="warning">💸</span>
                  <h4 className="text-lg font-extrabold text-blue-900">Reconciliation Excess Payment</h4>
                </div>
                
                <div className="text-sm text-gray-600 space-y-2 mb-6 border-y border-blue-50 py-3">
                  <p className="text-red-600 font-bold">The payment amount exceeds the pending amount of this invoice!</p>
                  <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-xs font-semibold pt-1">
                    <span className="text-gray-400">Invoice No:</span>
                    <span className="text-blue-900">{warningModal.invoiceNo}</span>
                    <span className="text-gray-400">Invoice Total:</span>
                    <span className="text-gray-900">₹{warningModal.invoiceAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    <span className="text-gray-400">Previously Paid:</span>
                    <span className="text-gray-900">₹{((warningModal.invoiceAmount || 0) - (warningModal.pendingAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    <span className="text-gray-400">Remaining Pending:</span>
                    <span className="text-blue-900">₹{warningModal.pendingAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    <span className="text-gray-400">Current Payment:</span>
                    <span className="text-green-700">₹{warningModal.receivedAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    {(warningModal.gatewayFee ?? 0) > 0 && (
                      <>
                        <span className="text-gray-400">Gateway Fee Logged:</span>
                        <span className="text-gray-700">₹{warningModal.gatewayFee?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </>
                    )}
                    <span className="text-gray-400">Excess Amount:</span>
                    <span className="text-red-600 font-extrabold">₹{warningModal.difference?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-3">Mapping this transaction will result in an overpayment on the invoice. Are you sure you want to proceed?</p>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setWarningModal(null)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition text-xs uppercase"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const tdsVal = Number(tdsInputVal || 0);
                      if (warningModal.type === 'credit') {
                        await handleMapRow(warningModal.rowId, warningModal.invoiceId || null, null, null, undefined, null, undefined, warningModal.remarks, tdsVal, false, null, undefined, null, null, null, undefined, warningModal.gatewayFee || 0);
                      } else {
                        await handleMapRow(warningModal.rowId, null, warningModal.purchaseId || null, null, undefined, null, undefined, warningModal.remarks, tdsVal, false, null, undefined, null, null, null, undefined, warningModal.gatewayFee || 0);
                      }
                      setEditMode(prev => ({ ...prev, [warningModal.rowId]: false }));
                      setWarningModal(null);
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition text-xs uppercase shadow-md hover:shadow-lg"
                  >
                    Confirm & Map
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 text-amber-600 mb-3">
                  <span className="text-2xl" role="img" aria-label="warning">⚠️</span>
                  <h4 className="text-lg font-extrabold text-blue-900">Reconciliation Difference</h4>
                </div>
                
                <div className="text-sm text-gray-600 space-y-2 mb-6 border-y border-blue-50 py-3">
                  <p>The transaction amount is less than the invoice amount:</p>
                  <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-xs font-semibold pt-1">
                    <span className="text-gray-400">Invoice No:</span>
                    <span className="text-blue-900">{warningModal.invoiceNo}</span>
                    <span className="text-gray-400">Invoice Total:</span>
                    <span className="text-gray-900">₹{warningModal.invoiceAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    {warningModal.pendingAmount !== undefined && warningModal.pendingAmount !== warningModal.invoiceAmount && (
                      <>
                        <span className="text-gray-400">Previously Paid:</span>
                        <span className="text-gray-900">₹{((warningModal.invoiceAmount || 0) - (warningModal.pendingAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        <span className="text-gray-400">Remaining Pending:</span>
                        <span className="text-blue-900">₹{warningModal.pendingAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </>
                    )}
                    <span className="text-gray-400">Transferred Amount:</span>
                    <span className={warningModal.type === 'credit' ? 'text-green-700' : 'text-red-700'}>
                      ₹{warningModal.receivedAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                    {(warningModal.gatewayFee ?? 0) > 0 && (
                      <>
                        <span className="text-gray-400">Gateway Fee Logged:</span>
                        <span className="text-gray-700">₹{warningModal.gatewayFee?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </>
                    )}
                    <span className="text-gray-400">Unresolved Difference:</span>
                    <span className="text-red-600 font-extrabold">
                      ₹{Math.max(0, (warningModal.difference || 0) - Number(tdsInputVal || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Specify TDS Deduction Amount (if any):
                    </label>
                    <input
                      type="number"
                      placeholder="Enter TDS Amount..."
                      value={tdsInputVal}
                      onChange={e => setTdsInputVal(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold bg-white text-gray-950"
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setWarningModal(null)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition text-xs uppercase"
                  >
                    Cancel
                  </button>

                  {warningModal.type === 'credit' ? (
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          const tdsVal = tdsInputVal !== "" ? Number(tdsInputVal) : warningModal.difference;
                          await handleMapRow(warningModal.rowId, warningModal.invoiceId || null, null, null, undefined, null, undefined, warningModal.remarks, tdsVal, false, null, undefined, null, null, null, undefined, warningModal.gatewayFee || 0);
                          setEditMode(prev => ({ ...prev, [warningModal.rowId]: false }));
                          setWarningModal(null);
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition text-xs uppercase shadow-md hover:shadow-lg"
                      >
                        Proceed considering TDS
                      </button>
                      <button
                        onClick={async () => {
                          const tdsVal = Number(tdsInputVal || 0);
                          await handleMapRow(warningModal.rowId, warningModal.invoiceId || null, null, null, undefined, null, undefined, warningModal.remarks, tdsVal, true, null, undefined, null, null, null, undefined, warningModal.gatewayFee || 0);
                          setEditMode(prev => ({ ...prev, [warningModal.rowId]: false }));
                          setWarningModal(null);
                        }}
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold transition text-xs uppercase shadow-md hover:shadow-lg"
                      >
                        Partial Payment
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        const tdsVal = Number(tdsInputVal || 0);
                        await handleMapRow(warningModal.rowId, null, warningModal.purchaseId || null, null, undefined, null, undefined, warningModal.remarks, tdsVal, true, null, undefined, null, null, null, undefined, warningModal.gatewayFee || 0);
                        setEditMode(prev => ({ ...prev, [warningModal.rowId]: false }));
                        setWarningModal(null);
                      }}
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold transition text-xs uppercase shadow-md hover:shadow-lg"
                    >
                      Partial Payment
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Row Deletion Warning Modal */}
      {showDeleteWarningModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs transition-all">
          <div className="bg-white rounded-3xl p-7 max-w-md w-full shadow-2xl border border-red-100 transform scale-100 transition-all">
            <div className="flex items-center gap-3 text-red-600 mb-3">
              <span className="text-2xl" role="img" aria-label="warning">⚠️</span>
              <h4 className="text-lg font-extrabold text-blue-900">Delete Row Confirmation</h4>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this bank statement row? This row will be soft-deleted. You can retrieve it later from the <strong>Deleted Data (Trash)</strong> tab if needed.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteWarningModal(false);
                  setDeleteWarningRowId(null);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition text-xs uppercase"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteRow}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition text-xs uppercase shadow-md"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatementUpload;
